import { saveAppliedTruth } from '../shared/applied-truth-transport';
import { inventoryAppliedFrame, pushInventoryTruth, retireTransferredInventory } from '../shared/inventory-applied-truth';
// Inventory equip plans and AP are browser-owned. The save keeps CAS/receipts and relays C0 base values.
import { apLegCost, EQUIP_RULE_CONSTANTS, equipFreeHandVerdict, equipSwapVerdict, slotLimitVerdict, waistBeltVerdict } from '../janus-engine/inventory/equip-rules.js';
import { actorInCombat, createEquipPageReaders, entryId } from './equip-page-readers';
import {
  applyEquippedToTree, carriedRows, equipBefore, equipItemEligible, equippedInSlot, handState, holdsStatus, planEquip, planHands, planTimedEquipFinish, planTimedEquipStart, timedStartOf, type EquipPlanResult,
  INVENTORY_EQUIP_ENDPOINT, INVENTORY_EQUIP_SCHEMA, type EquipPlanRow, type EquipRowState, type HandSlots, type InventoryEquipCommand, type InventoryEquipKind,
  type TimedEquipStart,
} from '../janus-engine/inventory/equip-plan.js';
import { C1_CONTRACT, compileCreatureEquippedCounts, possessorDependencyView, resolveEquipSlot, resolveEquipSwap } from '../janus-engine/compile/possessor-compile';
import { projectEquipmentDependencies } from '../janus-engine/compile/equipment-dependency-delta';
import { equipmentComputedDelta } from '../janus-engine/compile/saved-equipment-delta';
import { applyAppliedFrame, paintAppliedTree } from '../janus-engine/transport/apply-applied-frame';
import type { AppliedFrame } from '../janus-engine/transport/applied-frame';
import { gmStatusAppliedFrame } from '../janus-engine/transport/gm-applied-frame';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules';
import { readNpcCastableChargeFromBase } from '../janus-engine/compile/from-base-npc-castable';
import { phpJsonInt } from '../janus-engine/compile/php-json-values';
import { equipmentAcDecisionView, projectEquipmentArmorClass, installEquipmentArmorClass } from '../janus-engine/compile/creature-equipment-ac-delta';
import { ecId, HandBack, Refusal, type JanusCtx } from '../janus-engine/ability/context.js';
import { paoeEntryContext } from '../janus-engine/ability/paoe-entry.js';
import { frameCopyOnRead } from '../janus-engine/ability/frame-snapshot.js';
import { ftCampaignTimeAddSeconds, ftClockBaseFromArr, ftClockCalendarFromArr, ftDeriveCampaignClock } from '../shared/campaign-clock.js';
import { _ftCamField } from '../shared/campaign-field.js';
import { encounterCreatureField, encounterField, layerValue } from '../shared/array-types.js';
import { readInventoryField, readInventoryRows } from '../janus-engine/compile/inventory-raw-readers.js';
import { readItemField } from '../janus-engine/compile/item-field-reader';
import { answerConditionContext } from '../janus-engine/compile/condition-context';
import { stackTarget } from '../janus-engine/inventory/move-rules.js';
import { statusItemId, statusRows } from '../janus-engine/statuses/expiry.js';
import { browserRollSource } from '../janus-engine/actions/weapon-attack.js';
import { assignFreshStatusIds } from '../janus-engine/actions/status-row-identity.js';
import { splitStatusCopies } from '../janus-engine/inventory/trade-exchange.js';
import { findCarriedInventoryEntry } from '../janus-engine/inventory/find-inventory-entry.js';
import { cachedInventoryRows } from '../janus-engine/inventory/cached-inventory-trees';
import type { GmBoundaryVerdict } from '../janus-engine/statuses/gm-boundary.js';
import { timedEquipCampaignId, timedEquipTimerSkipped } from '../janus-engine/inventory/timed-equip-context.js';
import { ensureActorRowHeld } from '../shared/actor-row-loader.js';
import { applyApsScalar } from '../janus-engine/movement/client-truth-commit';
import { queueAppliedSave } from '../shared/applied-save-queue';
import { awaitInventoryBirth, INVENTORY_BIRTH_WAIT_WORDS } from '../shared/inventory-birth-persistence';
import { withAsyncDeadline } from '../shared/async-deadline';
import { inventoryDecisionView } from '../janus-engine/inventory/inventory-decision-view.js';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { applyEquipmentIntegerDelta } from '../janus-engine/compile/equipment-integer-delta';
import { createClassificationContext } from '../janus-engine/compile/from-base-classification-context';
import { inventoryRuntimeMetadata } from '../janus-engine/compile/inventory-runtime-metadata';
import { mergeInventoryMetadata } from '../janus-engine/compile/inventory-shared-catalog';
import { loadSpawnSources } from '../shared/spawn-browser-bridge';
// N6c: the move lane's decision, CALLED read-only (never edited here) — the bag-held equip chain checks it before anything moves.
import { decideInventoryMove, type ClientMoveDecision } from '../ft3d-move-handlers/move-decision';
import type { InventoryMoveReceipt } from '../ft3d-move-handlers/inventory-move-receipt';
type Row = Record<string, unknown>;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const nonEmptyLayer = (v: unknown): boolean => !(v === undefined || v === null || v === false || v === 0 || v === '' || v === '0' || (Array.isArray(v) && v.length === 0));

export interface InventoryEquipWireFrame {
  applied_frame?: AppliedFrame;
  schema: typeof INVENTORY_EQUIP_SCHEMA;
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  kind: InventoryEquipKind;
  rows: EquipPlanRow[];
  aps?: number;
  reason?: string;
  fanout?: string;
  provisional?: boolean;
  committed?: boolean;
  correction?: boolean;
  /** don / doff: after the post-flush fan-out (the status the start applied). */
  settled?: boolean;
  timed?: { status: string; status_id: string; inventory_id: string };
}
export interface InventoryEquipAnswer { missing?: string[]; ok: boolean; status: number; handback?: boolean; error?: string; message?: string; operation_id?: string;
  local?: true;
  /** Compatibility receipt label; equipment now settles through base_patch. */
  c1?: string }

const slots = (): HandSlots => {
  const s = C1_CONTRACT.equipmentSlots;
  return { main_hand: s.main_hand, off_hand: s.off_hand, both_hands: s.both_hands };
};

interface FtCaches { objectCache?: unknown; casterCache?: unknown; encounterCache?: unknown }
const ftCaches = (): FtCaches | undefined => (window as unknown as { FateTide?: FtCaches }).FateTide;

/** The compiled caster row this page holds for the actor: the caster cache first, else the encounter member. */
function casterRow(actorId: string): Row | null {
  const ft = ftCaches();
  const id = upper(actorId);
  if (ft?.casterCache instanceof Map) {
    for (const [key, value] of ft.casterCache.entries()) if (upper(key) === id && asRow(value)) return value as Row;
  }
  if (ft?.encounterCache instanceof Map) {
    for (const enc of ft.encounterCache.values()) {
      for (const m of (asRow(enc)?.encounter_creatures_arr as unknown[] | undefined) ?? []) {
        if (upper(asRow(asRow(m)?.encounter_creatures)?.encounter_creature_id) === id) return m as Row;
      }
    }
  }
  return null;
}

/** An inventory row anywhere the page holds it — the actor's tree, or another encounter member's (a dead body's worn gear, which a looter
 *  doffs in combat). */
function anyRow(id: string): Row | null {
  const ft = ftCaches();
  const ID = upper(id);
  let hit: Row | null = null;
  const walk = (nodes: unknown, depth: number): void => {
    if (hit || depth > 12) return;
    for (const n of Array.isArray(nodes) ? nodes : asRow(nodes) ? Object.values(asRow(nodes)!) : []) {
      const r = asRow(n); if (!r) continue;
      if (upper(asRow(r.inventory)?.inventory_id) === ID) { hit = r; return; }
      walk(r.inventory_in_container, depth + 1);
    }
  };
  if (ft?.casterCache instanceof Map) for (const v of ft.casterCache.values()) { walk(asRow(v)?.inventory, 0); if (hit) return hit; }
  if (ft?.encounterCache instanceof Map) for (const enc of ft.encounterCache.values()) {
    for (const m of (asRow(enc)?.encounter_creatures_arr as unknown[] | undefined) ?? []) { walk(asRow(m)?.inventory, 0); if (hit) return hit; }
  }
  return hit;
}

function cachedTimedEquipCampaign(caster: Row, encounter: Row | null): Row | null {
  const id = timedEquipCampaignId(caster, encounter), nested = asRow(caster.campaign_arr);
  if (upper(_ftCamField(nested, 'campaign_id')) === id && nested) return nested;
  const cache = (window as unknown as { FateTide?: { campaignCache?: Map<string, unknown> } }).FateTide?.campaignCache;
  return asRow(cache?.get(id) ?? cache?.get(id.toLowerCase()));
}

/** Load saved context through the existing row loader and campaign SELECT before deciding a timer. */
export async function loadTimedEquipContext(actorId: string): Promise<{ caster: Row; encounter: Row | null; campaign: Row | null }> {
  const sink = ftCurrentTruthSink();
  if (sink) {
    const caster = casterRow(actorId);
    const id = caster ? upper(encounterCreatureField(caster.encounter_creatures, 'encounter_id')) : '';
    const cache = ftCaches()?.encounterCache;
    const encounter = sink.heldBase?.(id) && cache instanceof Map ? asRow(cache.get(id) ?? cache.get(id.toLowerCase())) : null;
    const campaign = asRow(encounter?.campaign_arr);
    if (!caster || !encounter || !campaign) throw new Refusal('The practice equipment context is not held. Try the equipment again.');
    return { caster, encounter, campaign };
  }
  const where = await ensureActorRowHeld(actorId, undefined, { allowRowOnly: true, what: 'The equipment' });
  if (where.kind === 'refused') throw new Refusal(where.message);
  const cache = ftCaches()?.encounterCache;
  const encounter = where.kind === 'encounter' && cache instanceof Map ? asRow(cache.get(where.encounterId) ?? cache.get(where.encounterId.toLowerCase())) : null;
  const caster = casterRow(actorId) ?? where.row;
  const id = timedEquipCampaignId(caster, encounter);
  const w = window as unknown as { FateTide?: { campaignCache?: Map<string, unknown> }; getCampaignArr?: (id: string) => Promise<unknown> };
  let campaign = cachedTimedEquipCampaign(caster, encounter);
  if (id && !campaign) campaign = asRow(await w.getCampaignArr?.(id));
  if (id && !campaign) throw new Refusal('The campaign could not be loaded. Try the equipment again.');
  if (id && campaign && w.FateTide?.campaignCache instanceof Map) w.FateTide.campaignCache.set(id, campaign);
  return { caster, encounter, campaign };
}
export async function timerSkipped(caster: Row, _encounter: Row | null): Promise<boolean> {
  const held = await loadTimedEquipContext(ecId(caster));
  return timedEquipTimerSkipped(held.caster, held.encounter, held.campaign);
}
(window as unknown as { ftLoadTimedEquipContext: typeof loadTimedEquipContext }).ftLoadTimedEquipContext = loadTimedEquipContext;

async function timedStartPlan(caster: Row, encounter: Row | null, item: Row, kind: 'don' | 'doff', apCredit = 0) {
  const held = await loadTimedEquipContext(ecId(caster));
  const scene = encounter ?? {};
  const bridge = window as unknown as {
    ftInventoryEquipActivityReaders: (caster: Row, encounter: Row) => JanusCtx['read'];
    ftLoadActivityStatus: (encounterId: string, actorId: string, statusId: string) => Promise<Row | null>;
  };
  let read = bridge.ftInventoryEquipActivityReaders(caster, scene);
  const campaign = held.campaign;
  const calendar = ftClockCalendarFromArr(campaign);
  const clock = ftDeriveCampaignClock(ftClockBaseFromArr(campaign), Date.now()).cols;
  const status = kind === 'don' ? EQUIP_RULE_CONSTANTS.donning : EQUIP_RULE_CONSTANTS.doffing;
  if (!read.definition(status)) {
    try {
      const input = await loadSpawnSources([status], [], undefined, undefined, false);
      const inputs = mergeInventoryMetadata(input.shared, input.base);
      const definitions = createClassificationContext(inputs, inventoryRuntimeMetadata(input.metadata));
      const held = read;
      const definition = (id: string): Row | null => held.definition(id)
        ?? (asRow(inputs.catalog)?.[upper(id)] ? definitions.definition(id) : null);
      read = { ...held, definition };
    } catch {
      throw new Refusal('The equipment activity could not be loaded. Try equipping again.');
    }
  }
  let prior: Row | null;
  try {
    prior = await withAsyncDeadline(() => bridge.ftLoadActivityStatus(upper(encounterCreatureField(caster.encounter_creatures, 'encounter_id')), ecId(caster), status), 20_000);
  } catch { throw new Refusal('The equipment activity could not be loaded. Try equipping again.'); }
  // All reads that can yield finish before this synchronous activity decision.
  const members = new Map([[ecId(caster), caster]]);
  const ctx: JanusCtx = paoeEntryContext(scene, members, {
    ...read, campaignClock: () => clock,
    addCampaignSeconds: (value, seconds) => ftCampaignTimeAddSeconds(value, calendar, seconds),
    inCombat: () => actorInCombat(caster, encounter) === true,
  }, { d: () => { throw new Error('Timed equipment does not roll dice'); }, fraction: () => { throw new Error('Timed equipment does not roll dice'); } },
  () => frameCopyOnRead(scene, members, { catalogs: 'share' }));
  return planTimedEquipStart(ctx, caster, item, kind, createEquipPageReaders(caster, encounter), prior, apCredit);
}

const seen = new Map<string, Set<string>>();
function noteSeen(operationId: string, kind: string): boolean {
  const kinds = seen.get(operationId) ?? new Set<string>();
  if (kinds.has(kind) || (kind === 'provisional' && (kinds.has('committed') || kinds.has('correction')))) return false;
  kinds.add(kind); seen.set(operationId, kinds);
  if (seen.size > 256) seen.delete(seen.keys().next().value!);
  return true;
}
function patchCaches(rows: EquipPlanRow[]): number {
  let hits = 0;
  const ft = ftCaches();
  const visited = new Set<Row>();
  const planned = new Map(rows.map(row => [row.inventory_id, row]));
  for (const cache of [ft?.objectCache, ft?.casterCache, ft?.encounterCache]) if (cache instanceof Map) for (const value of cache.values()) {
    for (const row of cachedInventoryRows(value)) if (!visited.has(row)) {
      visited.add(row);
      const change = planned.get(upper(readInventoryField(row, 'inventory_id')));
      if (change && (upper(readInventoryField(row, 'equipped')) !== upper(change.equipped)
        || change.possess_id && upper(readInventoryField(row, 'possess_id')) !== upper(change.possess_id)))
        hits += applyEquippedToTree({ inventory: row.inventory }, [change]);
    }
  }
  return hits;
}

type PendingEquip = { actorId: string; rows: EquipPlanRow[]; aps: number; revision: number; dependenciesPending?: boolean; sent?: boolean; saved?: boolean };
const equipHost = window as unknown as { ftPendingEquips?: Map<string, PendingEquip>; ftPendingEquipListener?: boolean };
const pendingEquips = equipHost.ftPendingEquips ??= new Map<string, PendingEquip>();
/** A successful move supersedes this page's older slot intent, without adopting its dependencies. */
function supersedePendingInventoryEquip(inventoryId: string, ownerId: string | null, equipped = '', possessId?: string): void {
  const matches = (change: EquipPlanRow): boolean => upper(change.inventory_id) === upper(inventoryId);
  for (const [id, pending] of pendingEquips) {
    // A never-sent command still owns its later SQL write and expected slot.
    if (!pending.sent || !pending.rows.some(matches)) continue;
    pending.rows = upper(ownerId) === pending.actorId
      ? pending.rows.map(change => matches(change) ? { ...change, equipped, ...(possessId ? { possess_id: possessId } : {}) } : change)
      : pending.rows.filter(change => !matches(change));
    if (!pending.rows.length) pendingEquips.delete(id);
  }
}
function recountEquipped(actorId: string, changedRows: readonly EquipPlanRow[] = []): Set<Row> {
  const ft = ftCaches(), copies = new Set<Row>();
  const encounters = ft?.encounterCache instanceof Map ? [...ft.encounterCache.values()].map(asRow) : [];
  const encounter = encounters.find(enc => childrenOf(enc?.encounter_creatures_arr).some(row => ecId(asRow(row)) === actorId)) ?? null;
  const source = casterRow(actorId);
  if (!source) return copies;
  const read = createEquipPageReaders(source, encounter);
  const equipped = compileCreatureEquippedCounts(source, { definition: read.definition, catalog: asRow(source.catalog) ?? asRow(encounter?.catalog) });
  const consider = (value: unknown): void => {
    const row = asRow(value);
    if (!row || ecId(row) !== actorId || copies.has(row)) return;
    copies.add(row);
    retireEquippedDrawAttacks(row, encounter);
    if (!equipped) {
      const creature = asRow(asRow(row.computed)?.creature);
      if (creature) delete creature.equipped;
      return;
    }
    const computed = asRow(row.computed) ?? (row.computed = {}) as Row;
    const creature = asRow(computed.creature) ?? (computed.creature = {}) as Row;
    if (changedRows.length) applyEquipmentIntegerDelta(row, changedRows.map(change => change.inventory_id), read.definition);
    const prior = asRow(creature.equipped);
    const target = prior ?? (creature.equipped = {}) as Row;
    for (const key of Object.keys(target)) if (!(key in equipped)) delete target[key];
    for (const [key, value] of Object.entries(equipped)) if (target[key] !== value) target[key] = value;
  };
  for (const cache of [ft?.objectCache, ft?.casterCache, ft?.encounterCache]) if (cache instanceof Map) for (const value of cache.values()) {
    consider(value);
    for (const member of childrenOf(asRow(value)?.encounter_creatures_arr)) consider(member);
  }
  return copies;
}
function preservePendingEquips(changedActors = new Set<string>()): Set<Row> {
  for (const pending of pendingEquips.values()) {
    if (patchCaches(pending.rows)) changedActors.add(pending.actorId);
    const row = casterRow(pending.actorId), ec = asRow(row?.encounter_creatures);
    if (ec && Number(encounterCreatureField(ec, 'movement_rev') ?? 0) <= pending.revision) {
      applyApsScalar(ec, pending.aps); ec.movement_rev = pending.revision;
    }
  }
  const copies = new Set<Row>();
  for (const actorId of changedActors) for (const row of recountEquipped(actorId)) copies.add(row);
  return copies;
}
if (!equipHost.ftPendingEquipListener) {
equipHost.ftPendingEquipListener = true;
function supersedeCommittedInventoryMove(receipt: InventoryMoveReceipt): void {
  if (receipt?.schema !== 'ft3d.inventory-move-receipt.v1' || !receipt.ok) return;
  const { source, destination, roots } = receipt.move;
  if (upper(source.inventory_id) !== upper(destination.inventory_id) && (source.deleted_after || source.item_qty_after <= 0))
    supersedePendingInventoryEquip(source.inventory_id, null);
  supersedePendingInventoryEquip(destination.inventory_id, destination.deleted_after ? null : roots.destination,
    destination.equipped, destination.possess_id);
}
window.addEventListener('ft:inventory-move-receipt-applied', event => {
  supersedeCommittedInventoryMove((event as CustomEvent<InventoryMoveReceipt>).detail);
});
// Attack and ability receipt consumers project only saved inventory truth.
window.addEventListener('ft:inventory-move-receipt', event => {
  supersedeCommittedInventoryMove((event as CustomEvent<{ receipt: InventoryMoveReceipt }>).detail?.receipt);
});
window.addEventListener('ft:inventory-floor-move-truth-committed', event => {
  const { envelope, result } = (event as CustomEvent).detail ?? {};
  if (result?.ok === true && envelope?.hand_drop && !envelope.hand_drop.split)
    supersedePendingInventoryEquip(envelope.inventory_id, null);
});
window.addEventListener('ft:entity-base-compiled', event => {
  const detail = (event as CustomEvent).detail, computed = asRow(detail?.computed);
  if (!computed) return;
  if (detail.applied && !String(detail.operationId ?? '').startsWith('saved-equipment:')) return;
  const members = [computed, ...childrenOf(computed.encounter_creatures_arr).map(asRow)];
  for (const [id, pending] of pendingEquips) {
    if (!pending.dependenciesPending) continue;
    const member = members.find(row => row && ecId(row) === pending.actorId);
    if (!member) continue;
    const cache = ftCaches()?.encounterCache;
    const encounter = asRow(cache instanceof Map
      ? cache.get(upper(encounterCreatureField(member.encounter_creatures, 'encounter_id'))) : null);
    const base = heldEquipmentBase(member, encounter);
    const raw = childrenOf(base?.encounter_creatures_arr).map(asRow).find(row => row && ecId(row) === pending.actorId) ?? base;
    const changes = new Map([...pendingEquips.values()].filter(entry => entry.actorId === pending.actorId)
      .flatMap(entry => entry.rows.map(change => [change.inventory_id, change] as const)));
    const matches = (row: Row | null | undefined): boolean => !!row && [...changes.values()].every(change => {
      const item = findCarriedInventoryEntry(row, change.inventory_id, {
        entries: (host, key) => readInventoryRows(host, key as 'inventory' | 'inventory_in_container'), inventoryField: readInventoryField,
      });
      return !!item && upper(readInventoryField(item, 'equipped')) === upper(change.equipped);
    });
    if (!matches(raw) || !matches(member)) continue;
    pending.dependenciesPending = false;
    if (pending.saved) pendingEquips.delete(id);
  }
});
window.addEventListener('ft:inventory-base-compiled', () => {
  for (const row of preservePendingEquips())
    (window as unknown as { ftRenderCasterRowLocal?: (row: Row, paths: readonly string[]) => void }).ftRenderCasterRowLocal?.(row,
      ['inventory', 'computed.creature.equipped', 'encounter_creatures.aps', 'encounter_creatures.movement_rev']);
});
}

function heldEquipmentBase(caster: Row, encounter: Row | null): Row | null | undefined {
  const actorId = ecId(caster), encounterId = upper(encounterCreatureField(caster.encounter_creatures, 'encounter_id'));
  return window.ftReadHeldEntityBase?.(encounterId, 'encounter')?.base
    ?? window.ftReadHeldEntityBase?.(actorId, 'pc')?.base ?? window.ftReadHeldEntityBase?.(actorId, 'creature')?.base
    ?? asRow(asRow(encounter?._entity_base)?.base) ?? asRow(asRow(caster._entity_base)?.base);
}

export function handleInventoryEquipTruth(frame: InventoryEquipWireFrame): void {
  if (frame?.schema !== INVENTORY_EQUIP_SCHEMA || !frame.operation_id || !Array.isArray(frame.rows)) return;
  const kind = frame.settled ? 'settled' : frame.correction ? 'correction' : frame.committed ? 'committed' : 'provisional';
  if (!noteSeen(frame.operation_id, kind)) return;
  if (frame.applied_frame) retireTransferredInventory(frame.applied_frame);
  // Saved status/equipment deltas own settlement. An activity may already have
  // finished when this acknowledgement arrives; its absence is not stale state.
  if (kind === 'settled') {
    window.dispatchEvent(new CustomEvent('ft:inventory-equip-truth', { detail: { kind, frame } }));
    return;
  }
  if (kind === 'correction' && pendingEquips.has(frame.operation_id)) return; // the ordered save owns this correction
  const changedActors = new Set<string>();
  if (patchCaches(frame.rows) && frame.actor_id) changedActors.add(upper(frame.actor_id));
  for (const row of preservePendingEquips(changedActors))
    (window as unknown as { ftRenderCasterRowLocal?: (row: Row, paths: readonly string[]) => void }).ftRenderCasterRowLocal?.(row,
      ['inventory', 'computed.creature.equipped']);
  // A correction undoes a provisional paint only for the rows it names: rehydrate the actor from the server (review S3 #3).
  if (frame.actor_id && kind !== 'correction') recountEquipped(upper(frame.actor_id), frame.rows);
  if (kind === 'correction' && frame.actor_id) void healCaster(upper(frame.actor_id));
  window.dispatchEvent(new CustomEvent('ft:inventory-equip-truth', { detail: { kind, frame } }));
}

// Compact equipment frames and local decisions share this native AC installation.
// The equipment/integer owner settles its flags before dispatching this event.
window.addEventListener('ft:inventory-equip-truth', event => {
  const detail = (event as CustomEvent<{ frame?: InventoryEquipWireFrame }>).detail;
  const actorId = upper(detail?.frame?.actor_id);
  if (!actorId) return;
  const ft = ftCaches(), seen = new Set<Row>();
  const encounter = ft?.encounterCache instanceof Map ? [...ft.encounterCache.values()].map(asRow)
    .find(enc => childrenOf(enc?.encounter_creatures_arr).some(row => ecId(asRow(row)) === actorId)) ?? null : null;
  const install = (candidate: unknown, retried = false): void => {
    const row = asRow(candidate);
    if (!row || seen.has(row) || ecId(row) !== actorId) return;
    seen.add(row);
    const before = JSON.stringify(asRow(asRow(row.computed)?.creature)?.ac);
    const result = projectEquipmentArmorClass(row, createEquipPageReaders(row, encounter).definition);
    if (!result.ok) {
      if (!retried && result.reason === 'armorclass31:ac-definition-not-held' && result.missing?.length) {
        void warmArmorClassDefinitions({ ok: false, status: 0, error: result.reason, missing: result.missing }).then(asked => {
          if (asked) { seen.delete(row); install(row, true); }
          else window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason: result.reason, actorId } }));
        });
        return;
      }
      window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason: result.reason, actorId } }));
      return;
    }
    installEquipmentArmorClass(row, result);
    if (JSON.stringify(result.ac) !== before)
      (window as unknown as { ftRenderCasterRowLocal?: (row: Row, paths: readonly string[]) => void }).ftRenderCasterRowLocal?.(row,
        ['computed.creature.ac', 'computed.creature.damage_mitigation']);
  };
  for (const cache of [ft?.casterCache, ft?.objectCache, ft?.encounterCache]) if (cache instanceof Map) for (const value of cache.values()) {
    install(value);
    for (const member of childrenOf(asRow(value)?.encounter_creatures_arr)) install(member);
  }
});

/** A birth compiled before equip can still carry a sheathed-only attack. Retire
 * that source-bound entry after flags settle, before a PL claim fingerprints it. */
function retireEquippedDrawAttacks(row: Row, encounter: Row | null): void {
  const abilities = asRow(asRow(asRow(row.computed)?.creature)?.abilities), attacks = abilities?.attack_ability;
  if (!abilities || !Array.isArray(attacks)) return;
  const read = createEquipPageReaders(row, encounter);
  const inventory = new Map(readInventoryRows(row, 'inventory').map(node => [upper(readInventoryField(node, 'inventory_id')), node]));
  for (let index = attacks.length - 1; index >= 0; index--) {
    const source = upper(readItemField(asRow(attacks[index]), 'source')), def = read.definition(entryId(attacks[index]));
    if (!source || !def || !nonEmptyLayer(read.value(def, 'draw_sheathed_weapon_for_attack_ability'))) continue;
    const instance = inventory.get(source);
    if (!instance || !read.definition(upper(readInventoryField(instance, 'inventory_item_id')))) continue;
    const sheathed = answerConditionContext('draw-sheathed', [row, source], {
      encounter, campaign: null, abilityTypes: C1_CONTRACT.abilityTypes,
      equipmentSlots: C1_CONTRACT.equipmentSlots, definition: read.definition,
      value: read.value, entries: (host, key) => key === 'inventory' ? readInventoryRows(host, 'inventory') : read.entries(host, key),
    });
    if (!sheathed) attacks.splice(index, 1);
  }
  if (!attacks.length) delete abilities.attack_ability;
}

type CasterHeal = { fetchAndCacheCaster?: (id: string, o?: { skipEncounterSeed?: boolean }) => Promise<void>; ftRerenderPcSheet?: (id: string) => void };
/** A refusal / correction means this page's caster copy is behind SQL: hydrate the caster cache itself (not the sheet's HTML — its panes
 *  render from the cache), so the next command posts the saved rows and AP (Codex review S3 #3). */
async function healCaster(actorId: string): Promise<void> {
  const w = window as unknown as CasterHeal;
  try { await w.fetchAndCacheCaster?.(actorId, { skipEncounterSeed: true }); } catch { /* the WS caster_update still converges */ }
  try { w.ftRerenderPcSheet?.(actorId); } catch { /* optional */ }
}

/** Hold planning through local installation. Saves then occupy the shared actor outbox. */
const inflight = new Set<string>();

async function postCommand(command: InventoryEquipCommand): Promise<{ response: Response | null; body: Row | null }> {
  // A transport failure — or a success whose body cannot be read — may have committed: replay the SAME operation once; the server's
  // receipt answers it (review S2b rv3 #10).
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const { response, body, readable } = await withAsyncDeadline(async signal => {
        const response = await saveAppliedTruth(INVENTORY_EQUIP_ENDPOINT, command, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command), signal });
        let body: Row | null = null;
        let readable = true;
        try { body = asRow(await response.json()); } catch { readable = false; }
        return { response, body, readable };
      }, 20_000);
      if (attempt === 0 && ((response.ok && (!readable || body === null)) || (response.status >= 500 && response.status !== 503))) continue; // 503 = a deliberate refusal
      return { response, body };
    } catch {
      if (attempt === 0) { await new Promise<void>(r => setTimeout(r, 300)); continue; }
    }
  }
  return { response: null, body: null };
}

/** A practice save projects its own branches; the caller only paints the receipt's changed paths. */
async function commitLocalEquipment(command: InventoryEquipCommand): Promise<InventoryEquipAnswer> {
  const { response, body } = await postCommand(command);
  if (!response?.ok || body?.ok !== true) {
    const reason = String(body?.reason ?? 'The equipment could not be changed in this practice room.');
    return { ok: false, status: response?.status ?? 0, error: reason, message: reason };
  }
  const actor = casterRow(command.actor_id);
  if (actor) (window as unknown as { ftRenderCasterRowLocal?: (row: Row, paths: readonly string[]) => void })
    .ftRenderCasterRowLocal?.(actor, body.changed_paths as string[]);
  window.dispatchEvent(new CustomEvent('ft:inventory-equip-truth', { detail: { kind: 'committed', frame: body.frame } }));
  return { ok: true, status: response.status, operation_id: command.operation_id, local: true };
}

/** Status removal's existing Janus frame: the removed row is absent before its LOSS listeners run. */
async function timedEquipLossPlan(caster: Row, encounter: Row, status: Row, rows: EquipPlanRow[]) {
  const bridge = window as unknown as { ftPlanTimedEquipStatusRemoval?: (eid: string, aid: string, encounter: Row, status: Row) => Promise<GmBoundaryVerdict> };
  if (!bridge.ftPlanTimedEquipStatusRemoval) throw new HandBack('timed-finish:status-loss-trigger');
  const members = new Map(childrenOf(encounter.encounter_creatures_arr).map(asRow).filter((m): m is Row => !!m).map(m => [ecId(m), m]));
  members.set(ecId(caster), caster);
  // The saver finishes every affected holder before LOSS, including an item doffed from another member.
  const view = inventoryDecisionView(members);
  view.equipped(rows);
  const lossMembers = [...view.members.values()]; // dnr-clone: allow size=roster references only; why=boundary accepts an ordered list of borrowed members
  const lossEncounter = { ...encounter, encounter_creatures_arr: lossMembers };
  const boundary = await bridge.ftPlanTimedEquipStatusRemoval(upper(encounterField(encounter, 'encounter_id')), ecId(caster), lossEncounter, status);
  if (boundary.statusesAdded.length) {
    const read = (window as unknown as { ftInventoryEquipActivityReaders: (caster: Row, encounter: Row) => JanusCtx['read'] })
      .ftInventoryEquipActivityReaders(caster, lossEncounter);
    const ctx = paoeEntryContext(lossEncounter, new Map(lossMembers.map(member => [ecId(member), member])), read, browserRollSource());
    ctx.statusesAdded = boundary.statusesAdded; ctx.statusesRemoved = boundary.statusesRemoved;
    assignFreshStatusIds(ctx);
  }
  return boundary;
}

/** Mandatory, page-observed Donning/Doffing completion, using ordinary equip and move decisions. */
export async function commitTimedEquipFinish(input: { encounterId: string; actorId: string; statusId: string; inventoryId: string; donning: boolean }): Promise<InventoryEquipAnswer> {
  const actorId = upper(input.actorId), inventoryId = upper(input.inventoryId), statusId = upper(input.statusId);
  const handback = (error: string): InventoryEquipAnswer => ({ ok: false, status: 0, handback: true, error });
  let liveCaster = casterRow(actorId), liveItem = anyRow(inventoryId);
  const heldEncounter = (): Row | null => {
    const cache = ftCaches()?.encounterCache;
    return cache instanceof Map ? asRow(cache.get(upper(input.encounterId)) ?? cache.get(input.encounterId.toLowerCase())) : null;
  };
  let encounter = heldEncounter();
  if (!liveCaster || !liveItem || !encounter) {
    try {
      const held = await loadTimedEquipContext(actorId);
      liveCaster = casterRow(actorId) ?? held.caster;
      encounter = heldEncounter() ?? held.encounter;
      await (window as unknown as { ftAdoptInventoryBase?: (row: Row) => Promise<unknown> }).ftAdoptInventoryBase?.(liveCaster);
      liveItem = anyRow(inventoryId);
      if (!liveItem) {
        const loaded = asRow(await (window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject?.(inventoryId));
        if (upper(readInventoryField(loaded, 'inventory_id')) === inventoryId) liveItem = loaded;
      }
    } catch { /* One hydration attempt; the boundary logs the same named skip if still missing. */ }
  }
  if (!liveCaster || !liveItem || !encounter) return handback('timed-finish:inventory-context-missing');
  const view = inventoryDecisionView(new Map([[actorId, liveCaster]]));
  const caster = view.equipment(actorId, slots(), inventoryId);
  // A corpse/floor item need not be in the caster's tree. Pin its small mutable row bucket before definition/context awaits.
  const item = view.item(inventoryId) ?? { ...liveItem, inventory: { ...asRow(liveItem.inventory) } };
  if (upper(encounterCreatureField(caster.encounter_creatures, 'encounter_id')) !== upper(input.encounterId)) return handback('timed-finish:encounter-mismatch');
  const statuses = asRow(caster.statuses)?.target;
  const selected = (Array.isArray(statuses) ? statuses : asRow(statuses) ? Object.values(asRow(statuses)!) : []).map(asRow).find(s => upper(s?.status_id) === statusId);
  const status = selected ? { ...selected } : null;
  if (!status) return { ok: false, status: 409, error: 'timed-finish-stale' };
  let read = createEquipPageReaders(caster, encounter);
  if (!asRow(read.fat(item).item_layer_types) || read.provides(caster, 'triggers_when_caster_loses_status_effect') === null) {
    await warmEquipDefinitions(caster);
    read = createEquipPageReaders(caster, encounter);
  }
  const fat = read.fat(item), S = slots(), field = (key: string): unknown => readInventoryField(item, key);
  const rowOf = (id: string): Row | null => findCarriedInventoryEntry(caster, id, read);
  if (!asRow(fat.item_layer_types)) return handback('timed-finish:inventory-definition-missing');
  const loss = read.provides(caster, 'triggers_when_caster_loses_status_effect');
  if (loss === null) return handback('timed-finish:status-loss-trigger');
  const connection = upper(status.status_connected_id);
  if (!connection) return handback('timed-finish:status-connection-missing');
  const members = childrenOf(encounter.encounter_creatures_arr).map(asRow).filter((m): m is Row => !!m);
  for (const member of [caster, ...members]) for (const s of childrenOf(asRow(member.statuses)?.target).map(asRow)) {
    if (s && upper(s.status_connected_id) === connection && upper(s.status_id) !== statusId) return handback('timed-finish:status-connected-peers');
  }
  // Use the instant lane's carried-unit projection; the finish planner saves its move and equip in one transaction.
  const equipCaster = input.donning && upper(field('possess_id')) !== actorId
    ? inventoryDecisionView(new Map([[actorId, caster]])).held(actorId, inventoryId) : caster;
  if (!equipCaster) return handback('timed-finish:don-source-move');
  if (!input.donning && upper(field('possess_id')) !== actorId
    && !members.some(m => ecId(m) === upper(field('possess_id')))) return handback('timed-finish:doff-source-chain');
  if (!input.donning && childrenOf(item.inventory_in_container).length && upper(field('possess_id')) !== actorId) return handback('timed-finish:container-wares');
  const preferred = upper(layerValue(read.value(fat, 'equipment_slot')));
  let slot = preferred || upper(S.off_hand), stow: string[] = [], merge: Row | null = null;
  const hands = [S.main_hand, S.off_hand, S.both_hands].map(upper);
  if (input.donning) {
    if (nonEmptyLayer(field('inventory_unidentified'))) return handback('timed-finish:unidentified-soft-downgrade');
    if (hands.includes(slot)) {
      const planCtx = { definition: (id: string) => read.definition(id), catalog: asRow(caster.catalog) };
      const free = resolveEquipSlot(equipCaster, inventoryId, planCtx), swap = free === null ? resolveEquipSwap(equipCaster, inventoryId, planCtx) : null;
      if (free === null && !swap) return handback('timed-finish:hand-slot');
      slot = upper(free ?? swap!.slot); stow = (swap?.stow ?? []).map(upper);
    }
    const h = handState(carriedRows(caster), S);
    const unit = { ...fat, inventory: { ...asRow(fat.inventory), item_qty: 1 } };
    const verdict = stow.length ? equipSwapVerdict(caster, unit, read)
      : equipFreeHandVerdict(caster, unit, slot, S, { main: h.main ? read.fat(rowOf(h.main) ?? {}) : null, off: h.off ? read.fat(rowOf(h.off) ?? {}) : null }, read);
    if ('unknown' in verdict || !verdict.ok) return handback('timed-finish:equip-conditions');
    const limit = hands.includes(slot) ? { ok: true } : slotLimitVerdict(slot, equippedInSlot(caster, slot));
    if ('ok' in limit && !limit.ok) return handback('timed-finish:slot-limit');
    // One belt on the Waist (owner 2026-10-08; move_inventory's ft_waist_second_belt_refusal()).
    const belt = waistBeltVerdict(slot, fat, wornFatInSlot(caster, slot, read.fat, inventoryId), read);
    if ('ok' in belt && !belt.ok) return handback('timed-finish:slot-limit');
  } else {
    const candidates = childrenOf(caster.inventory).map(asRow).filter((r): r is Row => !!r && upper(readInventoryField(r, 'possess_id')) === actorId);
    const pick = stackTarget(fat, candidates.map(read.fat), { isFloor: false, x: 0, y: 0, z: 0 },
      upper(field('possess_id')) === actorId && Number(field('inventory_wares') ?? 0) ? 1 : 0,
      { ...read, inventoryField: (r, key) => readInventoryField(r ?? null, key), statusItemIds: r => [...statusRows(r, 'target'), ...statusRows(r, 'caster')].map(statusItemId) });
    if ('unknown' in pick) return handback('timed-finish:inventory-merge:' + pick.unknown);
    merge = pick.target ? candidates.find(r => upper(readInventoryField(r, 'inventory_id')) === pick.target) ?? null : null;
    if (merge) {
      const mergeId = upper(readInventoryField(merge, 'inventory_id'));
      view.pinItems([mergeId]); merge = view.item(mergeId);
    }
  }
  const hash = [...new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`timed-finish|${statusId}|${connection}`)))].map(n => n.toString(16).padStart(2, '0')).join('');
  const operationId = `${hash.slice(0, 8)}-${hash.slice(8, 12)}-4${hash.slice(13, 16)}-8${hash.slice(17, 20)}-${hash.slice(20, 32)}`.toUpperCase();
  const splitId = operationId.slice(0, -1) + (operationId.endsWith('F') ? 'E' : 'F');
  const plan = planTimedEquipFinish(input.donning ? 'finish_don' : 'finish_doff', item, actorId, slot, { slots: S, stow, splitId, merge });
  if (!plan.ok) return handback(plan.error);
  const parentId = String(encounterField(encounter, 'encounter_master_parent_id') ?? '');
  const camps = (window as unknown as { FateTide?: { campaignCache?: Map<string, unknown> } }).FateTide?.campaignCache;
  if (!parentId || !(camps instanceof Map) || !(camps.has(upper(parentId)) || camps.has(parentId.toLowerCase()))) return handback('timed-finish:expiry-context-missing');
  let boundary: Awaited<ReturnType<typeof timedEquipLossPlan>> | null = null;
  if (loss) {
    // The saver writes the finish before LOSS: drops must observe those exact rows, including a new split identity.
    const split = plan.rows.find(row => row.split_from);
    const lossCaster = { ...(split ? caster : equipCaster) };
    if (split) lossCaster.inventory = [...childrenOf(lossCaster.inventory),
      { ...item, inventory: { ...asRow(item.inventory), inventory_id: split.inventory_id }, inventory_in_container: [] }];
    try { boundary = await timedEquipLossPlan(lossCaster, encounter, status, plan.rows); }
    catch (error) {
      window.dispatchEvent(new CustomEvent('ft:unported', { detail: { reason: 'timed-finish:status-loss-trigger', mode: 'refused',
        detail: { encounterId: upper(input.encounterId), actorId, timed_finish_error: String(asRow(error)?.message ?? error) } } }));
      return handback('timed-finish:status-loss-trigger');
    }
    if (!boundary) return handback('timed-finish:status-loss-trigger');
  }
  let splitStatuses: NonNullable<NonNullable<InventoryEquipCommand['timed_finish']>['split_statuses']> = [];
  if (plan.rows.some(r => r.split_from) && statusRows(item, 'target').length) {
    const bridge = window as unknown as { ftInventoryEquipActivityReaders: (caster: Row, encounter: Row) => JanusCtx['read'] };
    const ctx = { read: bridge.ftInventoryEquipActivityReaders(caster, encounter), roll: browserRollSource() };
    splitStatuses = splitStatusCopies(ctx, statusRows(item, 'target'), inventoryId, splitId);
  }
  const beforeRow = (row: Row) => ({ inventory_id: upper(readInventoryField(row, 'inventory_id')), possess_id: upper(readInventoryField(row, 'possess_id')),
    equipped: upper(readInventoryField(row, 'equipped')) });
  const beforeRows = [beforeRow(item), ...plan.rows.filter(r => r.inventory_id !== inventoryId && !r.split_from).map(r => beforeRow(rowOf(r.inventory_id)!))];
  for (const row of beforeRows) if (plan.rows.some(r => r.inventory_id === row.inventory_id && r.item_qty !== undefined))
    Object.assign(row, { item_qty: Number(readInventoryField(row.inventory_id === inventoryId ? item : rowOf(row.inventory_id), 'item_qty')) });
  const command: InventoryEquipCommand = { schema: INVENTORY_EQUIP_SCHEMA, operation_id: operationId, actor_id: actorId,
    kind: input.donning ? 'finish_don' : 'finish_doff', inventory_id: inventoryId,
    before: { rows: beforeRows, aps: null },
    plan: { rows: plan.rows }, ap: { cost: 0, in_combat: true },
    timed_finish: { status_id: statusId, status_connected_id: connection, log_expired: true, inventory_qty: Number(field('item_qty')), status_name: String(asRow(asRow(status.status_arr)?.items)?.item_name ?? asRow(status.items)?.item_name ?? ''),
      ...(boundary ? { boundary } : {}), ...(splitStatuses.length ? { split_statuses: splitStatuses } : {}) } };
  if (ftCurrentTruthSink()) return commitLocalEquipment(command);
  const finishWire = { ...command, encounter_id: upper(input.encounterId), rows: plan.rows };
  const finishFrame = inventoryAppliedFrame(finishWire, [
    ...plan.rows.map(change => ({ table: 'inventory' as const, id: change.inventory_id, before: change.split_from ? null : {}, after: { ...change } })),
    ...splitStatuses.map(copy => copy.write),
  ]);
  finishFrame.deletes = [...finishFrame.deletes, { table: 'statuses', row_id: statusId }];
  if (boundary) {
    const members = new Map(childrenOf(encounter.encounter_creatures_arr).map(asRow).filter((row): row is Row => !!row).map(row => [ecId(row), row]));
    const projection = frameCopyOnRead(encounter, members, { catalogs: 'share' }).encounter;
    for (const member of childrenOf(projection.encounter_creatures_arr).map(asRow)) if (member) applyEquippedToTree(member, plan.rows);
    const read = createEquipPageReaders(caster, encounter);
    const loss = gmStatusAppliedFrame({ ...finishFrame,
      rev: Number(encounterCreatureField(caster.encounter_creatures, 'movement_rev') ?? 0) + 1 }, boundary, projection,
      cachedTimedEquipCampaign(caster, encounter), read.definition);
    if (!('cache_ref' in loss)) {
      finishFrame.rows = [...finishFrame.rows, ...loss.rows];
      finishFrame.deletes = [...finishFrame.deletes, ...loss.deletes];
      finishFrame.computed_delta = loss.computed_delta; finishFrame.inputs = loss.inputs;
      finishFrame.new_entities = loss.new_entities;
      finishFrame.new_inventory = [...(finishFrame.new_inventory ?? []), ...(loss.new_inventory ?? [])];
      finishFrame.rev = loss.rev; finishFrame.member_revs = loss.member_revs;
    }
  }
  pushInventoryTruth('janus_inventory_equip', finishWire, finishFrame);
  const { response, body } = await postCommand(command);
  if (!response) return { ok: false, status: 0, error: 'network' };
  if (response.ok && body?.ok === true) {
    patchCaches(plan.rows);
    const holder = upper(field('possess_id'));
    if (boundary) (window as unknown as { ftTimedEquipStatusRemovalCommitted?: (eid: string, aid: string, verdict: GmBoundaryVerdict) => void })
      .ftTimedEquipStatusRemovalCommitted?.(upper(input.encounterId), actorId, boundary);
    if (holder === upper(input.encounterId) && Number(field('item_qty') ?? 1) === 1) {
      window.dispatchEvent(new CustomEvent('ft:inventory-move-receipt-applied', { detail: {
        schema: 'ft3d.inventory-move-receipt.v1', ok: true, mutation_id: operationId,
        move: { outcome: 'full_move', moved_qty: 1,
          source: { inventory_id: inventoryId, possess_id_before: holder, item_qty_after: 1, deleted_after: false },
          destination: { inventory_id: inventoryId, possess_id: actorId, item_qty_after: 1, created: false, equipped: slot,
            position: { x: 0, y: 0, z: 0 } }, roots: { source: null, destination: actorId } },
        affected_caster_ids: [actorId], affected_encounter_ids: [upper(input.encounterId)], timing: { accepted_unix_ms: Date.now(), commit_ms: 0 },
      } }));
    }
    return { ok: true, status: response.status, operation_id: operationId };
  }
  return { ok: false, status: response.status, error: String(body?.error ?? 'error'), handback: response.status === 422 && body?.error === 'handback' };
}
const childrenOf = (v: unknown): unknown[] => Array.isArray(v) ? v : asRow(v) ? Object.values(asRow(v)!) : [];
/** The FAT rows worn top-level in `slot` (equippedInSlot()'s rows, minus `exclude`) — the Waist's one-belt rule reads what they are. */
function wornFatInSlot(caster: Row, slot: string, fat: (row: Row) => Row, exclude = ''): Row[] {
  const ids = new Set(carriedRows(caster).filter(r => r.depth === 0 && r.equipped !== '' && r.equipped === upper(slot) && r.inventory_id !== upper(exclude))
    .map(r => r.inventory_id));
  return childrenOf(caster.inventory).map(asRow).filter((r): r is Row => !!r && ids.has(upper(asRow(r.inventory)?.inventory_id))).map(r => fat(r));
}

/** A hand-back that usually means a COLD page (a feat / grant / template definition it never fetched), not an undecidable rule. */
const COLD_PAGE = /^(rule:|ap:fast-draw-unknown$|no-plan$)/;
/** N6b (R-088 / R-089 / R-101): fetch the definitions the equip readers would read and the page lacks, once (bounded). Two hops (Codex N6b
 *  review P2): a feat fetched in hop 1 names its grants, fetched in hop 2. 48 definitions in all, each once. True = something was asked. */
async function warmEquipDefinitions(caster: Row): Promise<boolean> {
  const encId = upper(asRow(caster.encounter_creatures)?.encounter_id);
  const encCache = ftCaches()?.encounterCache;
  const enc = encCache instanceof Map ? asRow(encCache.get(encId) ?? encCache.get(encId.toLowerCase())) : null;
  const fetchObject = (window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
  const asked = new Set<string>();
  for (let hop = 0; hop < 2 && typeof fetchObject === 'function'; hop++) {
    const missing = createEquipPageReaders(caster, enc).missingDefinitions().filter(id => !asked.has(id)).slice(0, 48 - asked.size);
    if (!missing.length) break;
    for (const id of missing) asked.add(id);
    await Promise.all(missing.map(id => fetchObject(id).catch(() => null)));
  }
  return asked.size > 0;
}

/** Hydrate only the AC definitions named by the native preflight, never the held actor. */
async function warmArmorClassDefinitions(answer: InventoryEquipAnswer): Promise<boolean> {
  const fetchDefinition = (window as unknown as { fetchAndCacheObject?: (id: string) => Promise<unknown> }).fetchAndCacheObject;
  if (answer.error !== 'armorclass31:ac-definition-not-held' || !answer.missing?.length || !fetchDefinition) return false;
  await Promise.all(answer.missing.map(id => fetchDefinition(id).catch(() => null)));
  return true;
}

/** N6c (R-089 / R-101): `apCredit` = AP a chained leg already paid for this equip (the move out of a bag — equipInventory), taken off the
 *  equip leg so the chain costs what the one legacy equip did; `dryRun` plans + judges every rule and the AP, then stops before the C1
 *  build and the post (the chain's pre-check before anything moves). */
export interface EquipCommitInput { kind: Exclude<InventoryEquipKind, 'finish_don' | 'finish_doff'>; actorId?: string | null; inventoryId?: string | null; apCredit?: number; dryRun?: boolean; casterOverride?: Row;
  /** Internal (equipInventory): the chain's lock token — the chain already holds the actor's `inflight` slot (Codex N6c review P2 #3). */
  chainToken?: symbol }
/** The equip chain currently holding each actor's `inflight` slot (equipInventory) — only its token may commit inside it. */
const chainTokens = new Map<string, symbol>();
/** Out-of-bundle seam: commit one equip-family change. */
export async function commitInventoryEquip(input: EquipCommitInput): Promise<InventoryEquipAnswer> {
  try { return await commitHeldInventoryEquip(input); }
  catch (error) {
    return { ok: false, status: 422, error: 'refused', message: error instanceof Refusal ? error.refusal
      : error instanceof HandBack ? error.reason : 'The equipment could not be prepared. Try equipping again.' };
  }
}

async function commitHeldInventoryEquip(input: EquipCommitInput): Promise<InventoryEquipAnswer> {
  const handback = (why: string): InventoryEquipAnswer => ({ ok: false, status: 0, handback: true, error: why });
  const actorId = upper(input.actorId || (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId?.() || '');
  if (!actorId) return handback('no-caster-row');
  // Inside a chain (its token): the chain holds the actor's slot; an AP credit is honoured only there.
  const inChain = !!input.chainToken && chainTokens.get(actorId) === input.chainToken;
  if (!inChain && input.apCredit) input = { ...input, apCredit: 0 };
  // N6 (2026-10-01): a page that does not hold the actor's row — or its encounter (the combat state the plan needs) — loads them through
  // the shared actor-row loader instead of handing the click back; a row it cannot load is refused with words.
  const stored = upper(asRow(casterRow(actorId)?.encounter_creatures)?.encounter_id);
  const encCache = ftCaches()?.encounterCache;
  const encounterHeld = !stored || (encCache instanceof Map && !!(encCache.get(stored) ?? encCache.get(stored.toLowerCase())));
  if (!casterRow(actorId) || !encounterHeld) {
    const where = await ensureActorRowHeld(actorId, undefined, { allowRowOnly: true, what: 'The equipment' });
    if (where.kind === 'refused') return { ok: false, status: 0, error: 'refused', message: where.message };
  }
  if (!casterRow(actorId)) return handback('no-caster-row');
  if (input.dryRun) {
    // The chain's pre-check: one plan against the snapshot it names (the row as if already taken out), no claim, no post, no inflight.
    const snapshot = inventoryDecisionView(new Map([[actorId, (input.casterOverride ?? casterRow(actorId))!]]))
      .equipment(actorId, slots(), upper(input.inventoryId));
    const dry = await planAndCommit(input, actorId, 'DRY-RUN', snapshot, handback);
    if (await warmArmorClassDefinitions(dry)) {
      const fresh = inventoryDecisionView(new Map([[actorId, (input.casterOverride ?? casterRow(actorId))!]])).equipment(actorId, slots(), upper(input.inventoryId));
      return planAndCommit(input, actorId, 'DRY-RUN', fresh, handback);
    }
    return dry.handback && COLD_PAGE.test(String(dry.error ?? '')) && await warmEquipDefinitions(snapshot)
      ? planAndCommit(input, actorId, 'DRY-RUN', snapshot, handback) : dry;
  }
  if (!inChain && inflight.has(actorId)) return { ok: false, status: 0, error: 'pending', message: 'Your last equip is still being saved.' };
  const operationId = (crypto.randomUUID?.() ?? '').toUpperCase();
  if (!operationId) return handback('no-uuid');
  if (!inChain) inflight.add(actorId);
  try {
    if (input.inventoryId) {
      const answer = await awaitInventoryBirth(input.inventoryId);
      if (!answer.ok) return { ok: false, status: 0, error: 'inventory-birth-refused',
        message: INVENTORY_BIRTH_WAIT_WORDS };
    }
    // Instant equipment plans and installs in one task. Timed planning retains its snapshot
    // across context loads; a bag chain supplies its already isolated planning row.
    const live = inChain && input.casterOverride ? input.casterOverride : casterRow(actorId);
    if (!live) return handback('no-caster-row');
    const item = input.inventoryId ? anyRow(input.inventoryId) : null;
    const read = createEquipPageReaders(live, null);
    const timed = item && ['don', 'doff'].some(kind => timedStartOf(kind as 'don' | 'doff', key => read.value(read.fat(item), key)));
    const caster = timed ? inventoryDecisionView(new Map([[actorId, live]])).equipment(actorId, slots(), upper(input.inventoryId)) : live;
    let first = await planAndCommit(input, actorId, operationId, caster, handback);
    if (await warmArmorClassDefinitions(first)) {
      const fresh = inChain && input.casterOverride ? input.casterOverride : casterRow(actorId);
      if (fresh) first = await planAndCommit(input, actorId, operationId, fresh, handback);
    }
    // N6b (R-088 / R-089 / R-101): a rule the page could not judge ('rule:*', an undecidable Fast Draw, no slot plan) is usually a COLD page
    // — a feat / grant / template definition it never fetched. Fetch those once (bounded) and re-plan with a fresh operation id; only a
    // genuinely undecidable case (a conditioned feat) still hands back.
    if (first.handback && COLD_PAGE.test(String(first.error ?? ''))) {
      const retryId = (crypto.randomUUID?.() ?? '').toUpperCase();
      if (await warmEquipDefinitions(caster) && retryId) first = await planAndCommit(input, actorId, retryId, caster, handback);
    }
    return first;
  } finally {
    if (!inChain) inflight.delete(actorId);
  }
}

async function planAndCommit(input: EquipCommitInput, actorId: string, operationId: string, caster: Row,
  handback: (why: string) => InventoryEquipAnswer): Promise<InventoryEquipAnswer> {
  const inventoryId = upper(input.inventoryId ?? '');
  const S = slots();
  const encounterId = upper(asRow(caster.encounter_creatures)?.encounter_id);
  const ft = ftCaches();
  const encounter = ft?.encounterCache instanceof Map ? asRow(ft.encounterCache.get(encounterId) ?? ft.encounterCache.get(encounterId.toLowerCase())) : null;
  const read = createEquipPageReaders(caster, encounter);
  const combat = actorInCombat(caster, encounter);
  if (combat === null) return handback('combat-unknown'); // the page does not hold the actor's encounter (review S2b rv3 #5)
  const inCombat = combat;
  const rowOf = (id: string): Row | null => {
    let hit: Row | null = null;
    const walk = (nodes: unknown, depth: number): void => {
      if (hit || depth > 6) return;
      for (const n of Array.isArray(nodes) ? nodes : asRow(nodes) ? Object.values(asRow(nodes)!) : []) {
        const r = asRow(n); if (!r) continue;
        if (upper(asRow(r.inventory)?.inventory_id) === upper(id)) { hit = r; return; }
        walk(r.inventory_in_container, depth + 1);
      }
    };
    walk(caster.inventory, 0);
    return hit;
  };
  const refuse = (message: string): InventoryEquipAnswer => ({ ok: false, status: 422, error: 'refused', message });
  // The browser decides the plan and AP; PHP saves the rows and relays base values.
  let plan: EquipPlanResult; let primary = ''; let apCost: number | null = 0;
  let kind: InventoryEquipKind = input.kind; let timed: TimedEquipStart | null = null; let before: { rows: EquipRowState[]; aps: number | null } | null = null;
  let bodyHolder = ''; // a doff of a dead body's worn row: its holder is healed too on a refusal (Codex review donning #1)
  const hands = new Set([S.main_hand, S.off_hand, S.both_hands].map(upper));
  if (input.kind === 'equip' || input.kind === 'don') {
    if (!inventoryId) return handback('no-item');
    if (!equipItemEligible(caster, inventoryId, actorId)) return handback('not-a-single-held-row'); // a stack / a row in a bag / sheath
    const item = rowOf(inventoryId);
    if (!item) return handback('no-row');
    const fatItem = read.fat(item);
    // Donning lane (2026-09-29): gear with a donning time STARTS donning (move_inventory's needs_donning_or_doffing(): an equip onto the
    // holder itself, the slot resolved and validated first) — except at the campaign's campsite / stronghold, where it equips at once.
    const donTimeRaw = timedStartOf('don', k => read.value(fatItem, k));
    // N6b (R-088 / R-101): where the legacy route skips the timer (campsite / stronghold / no campaign) the browser plans the SAME instant
    // equip it makes (the non-timed branch below), after loading any missing saved context.
    const skipCtx = donTimeRaw ? await timerSkipped(caster, encounter)
      : timedEquipTimerSkipped(caster, encounter, cachedTimedEquipCampaign(caster, encounter));
    const skip = donTimeRaw ? skipCtx : false;
    const donTime = skip ? null : donTimeRaw;
    // The PLAN is the browser's own (possessor-compile's resolveEquipSlot / resolveEquipSwap — equip_or_swap's rules), independent of the
    // Saved base ops repaint the resulting inventory tree.
    const planCtx = { definition: (id: string) => read.definition(id), catalog: asRow(caster.catalog) };
    const freeSlot = resolveEquipSlot(caster, inventoryId, planCtx);
    const swapPlan = freeSlot === null ? resolveEquipSwap(caster, inventoryId, planCtx) : null;
    if (freeSlot === null && swapPlan === null) return handback('no-plan');
    const to = { equipped: upper(freeSlot ?? swapPlan!.slot) };
    const stow = (swapPlan?.stow ?? []).map(upper);
    plan = planEquip(inventoryId, to.equipped, stow, S);
    if (!plan.ok) return plan.handback ? handback(plan.error) : refuse(plan.error);
    // validate_equip_conditions (free hand, worn slots too) / size + heavy (the swap) — the browser's rules now.
    const h = handState(carriedRows(caster), S);
    const verdict = stow.length
      ? equipSwapVerdict(caster, fatItem, read)
      : equipFreeHandVerdict(caster, fatItem, upper(to.equipped), S, { main: h.main ? read.fat(rowOf(h.main) ?? {}) : null, off: h.off ? read.fat(rowOf(h.off) ?? {}) : null }, read);
    if ('unknown' in verdict) return handback('rule:' + verdict.unknown);
    if (!verdict.ok) return refuse(verdict.error);
    // check_slot_limit() — a worn slot (ring 2, waist 3, the rest 1); a free hand is free by construction.
    if (!hands.has(to.equipped)) {
      const limit = slotLimitVerdict(to.equipped, equippedInSlot(caster, to.equipped));
      if ('ok' in limit && !limit.ok) return refuse(limit.error);
      // One belt on the Waist (the defined belts), other waist items beside it — owner 2026-10-08 (ft_waist_second_belt_refusal()).
      const belt = waistBeltVerdict(to.equipped, fatItem, wornFatInSlot(caster, to.equipped, read.fat, inventoryId), read);
      if ('ok' in belt && !belt.ok) return refuse(belt.error);
    }
    primary = inventoryId;
    if (donTime && !stow.length) {
      // A DON start leaves the item in place; the page-observed finish equips it when the status ends.
      // (A donning-time HAND item with both hands full is equip_or_swap's instant swap — the legacy route never dons it.)
      if (holdsStatus(caster, EQUIP_RULE_CONSTANTS.donning)) return refuse('You are already donning something — finish that first.');
      kind = 'don';
      timed = { ...donTime, campsite: false, activity: await timedStartPlan(caster, encounter, fatItem, 'don', input.apCredit) };
      plan = { ok: true, rows: [] };
      if (inCombat) apCost = apLegCost(caster, fatItem, 'equip', read);
    } else {
      if (input.kind === 'don' && !skip) return handback('no-donning-time');
      kind = 'equip'; // a skipped timer equips at once (N6b)
      if (inCombat) {
        const legs = [...stow.map(id => apLegCost(caster, read.fat(rowOf(id) ?? {}), 'stow', read, skipCtx === true)), apLegCost(caster, fatItem, 'equip', read, skipCtx === true)];
        apCost = legs.some(c => c === null) ? null : legs.reduce<number>((a, c) => a + (c ?? 0), 0);
      }
    }
  } else if (input.kind === 'doff') {
    // Donning lane (2026-09-29): a move of worn gear with a doffing time STARTS doffing (needs_donning_or_doffing(): move_inventory of an
    // equipped row) — the actor's own, or a dead body's in combat (the corpse-loot rule: the looter pays the doff). The move's destination
    // is not kept: PHP's completion hands the item to the doffer, unequipped (timed_activity_completion.php, finish_doffing).
    if (!inventoryId) return handback('no-item');
    const item = rowOf(inventoryId) ?? anyRow(inventoryId);
    if (!item) return handback('no-row');
    const fatItem = read.fat(item);
    const bucket = asRow(item.inventory) ?? {};
    if (!nonEmptyLayer(bucket.equipped)) return handback('not-worn');
    const holder = upper(bucket.possess_id);
    if (holder !== actorId) {
      // A body in this encounter (the server re-proves it from SQL: the move authority's dead-holder rule).
      const body = (encounter?.encounter_creatures_arr as unknown[] | undefined ?? []).map(asRow).find(m => upper(asRow(m?.encounter_creatures)?.encounter_creature_id) === holder);
      if (!body || Number(asRow(body.encounter_creatures)?.encounter_creature_dead ?? 0) !== 1) return handback('not-own-or-body');
    }
    const doffTime = timedStartOf('doff', k => read.value(fatItem, k));
    if (!doffTime) return handback('no-doffing-time');
    const skipTimer = await timerSkipped(caster, encounter);
    const bodyOutOfCombat = holder !== actorId && !inCombat && Number(encounterField(encounter, 'in_combat') ?? 0) !== 1;
    if (skipTimer || bodyOutOfCombat) {
      const move = (window as unknown as { ftMoveInventoryWithReceipt?: MoveWithReceipt }).ftMoveInventoryWithReceipt;
      const result = await move?.(inventoryId, 1, holder, actorId, 'move_inventory', 1, null, null);
      return result && !isMoveRefusal(result) ? { ok: true, status: 200 } : refuse('The equipment move could not be confirmed.');
    }
    if (holdsStatus(caster, EQUIP_RULE_CONSTANTS.doffing)) return refuse('You are already doffing something — finish that first.');
    primary = inventoryId;
    timed = { ...doffTime, campsite: false, activity: await timedStartPlan(caster, encounter, fatItem, 'doff', input.apCredit) };
    plan = { ok: true, rows: [] };
    if (inCombat) apCost = apLegCost(caster, fatItem, 'stow', read);
    const base = equipBefore(caster, S, holder === actorId ? inventoryId : '');
    if (holder !== actorId) base.rows = [...base.rows, { inventory_id: inventoryId, possess_id: holder, equipped: upper(bucket.equipped) }].sort((a, b) => a.inventory_id.localeCompare(b.inventory_id));
    before = base;
    if (holder !== actorId) bodyHolder = holder;
  } else {
    plan = planHands(input.kind, caster, S);
    if (!plan.ok) return plan.handback ? handback(plan.error) : refuse(plan.error);
    const [first, ...others] = plan.rows;
    primary = first!.inventory_id;
    if (inCombat) apCost = input.kind === 'swap_hands' ? 1 : apLegCost(caster, read.fat(rowOf(primary) ?? {}), 'equip', read);
  }
  if (apCost === null) return handback('ap:fast-draw-unknown');
  // N6c: a chained leg (the move out of a bag) already paid part of this equip — the equip leg pays the rest, never below 0.
  if (inCombat && input.apCredit) apCost = Math.max(0, apCost - Math.max(0, Math.trunc(input.apCredit)));
  if (inCombat && apCost > Number(asRow(caster.encounter_creatures)?.aps ?? 0)) return refuse(`In Combat: This action requires ${apCost} AP!`);
  if (!timed && plan.rows.length) {
    const decision = equipmentAcDecisionView(caster, plan.rows);
    const armorClass = projectEquipmentArmorClass(decision, createEquipPageReaders(caster, encounter).definition);
    if (!armorClass.ok) return { ok: false, status: 0, error: armorClass.reason, message: armorClass.reason, missing: armorClass.missing };
  }
  if (input.dryRun) return { ok: true, status: 0, error: 'dry-run' };
  const command: InventoryEquipCommand = {
    schema: INVENTORY_EQUIP_SCHEMA, operation_id: operationId, actor_id: actorId, kind, inventory_id: primary,
    before: before ?? equipBefore(caster, S, primary),
    plan: { rows: plan.rows },
    ap: { cost: inCombat ? apCost : 0, in_combat: inCombat },
    ...(timed ? { timed } : {}),
  };
  // Practice saves both starts and slot changes before painting; its sink already projects the affected branches.
  if (ftCurrentTruthSink()) return commitLocalEquipment(command);
  if (!timed) {
    let copies: Set<Row> | null = null;
    let pushedDelta: AppliedFrame | undefined;
    const dependencyPaths: string[] = [];
    if (kind === 'equip') {
      // Project supported dependencies now; other carriers keep the local equip
      // and defer PL until their saved base and dependency adoption agree.
      const view = possessorDependencyView(caster);
      const raw = heldEquipmentBase(caster, encounter);
      const rawMember = childrenOf(raw?.encounter_creatures_arr).map(asRow).find(row => row && ecId(row) === actorId) ?? raw;
      const definition = (id: string): Row | null => fullDefinition(asRow(asRow(caster.catalog)?.[upper(id)]), 'any')
        ?? fullDefinition(asRow(asRow(encounter?.catalog)?.[upper(id)]), 'any') ?? read.definition(id)
        ?? fullDefinition(asRow(asRow(raw?.catalog)?.[upper(id)]), 'any');
      const projected = projectEquipmentDependencies(caster, view, plan.rows, { catalog: asRow(caster.catalog) ?? asRow(encounter?.catalog), definition,
        conditionScene: () => ({ encounter, campaign: cachedTimedEquipCampaign(caster, encounter) }),
        roster: () => childrenOf(encounter?.encounter_creatures_arr).map(asRow).filter((row): row is Row => !!row),
        ...(rawMember && Array.isArray(rawMember.charges) ? { charges: (holder: string, id: string) => {
          const current = readNpcCastableChargeFromBase(rawMember, holder, id, null);
          return current === null ? null : phpJsonInt(current);
        } } : {}) });
      if (projected.ok) {
        const delta: AppliedFrame = { kind: 'applied', operation_id: operationId, actor_id: actorId, encounter_id: encounterId,
          rev: 0, member_revs: {}, rows: [], computed: {}, computed_delta: equipmentComputedDelta(caster, view), new_entities: [], deletes: [],
          inputs: { catalog: {}, member_catalogs: { [actorId]: view.catalog as Row } } };
        pushedDelta = delta;
        dependencyPaths.push(...Object.keys(projected.clusters).filter(path => path !== 'inventory'));
        copies = new Set<Row>();
        for (const cache of [ft?.objectCache, ft?.casterCache, ft?.encounterCache]) if (cache instanceof Map) for (const value of cache.values()) {
          const host = asRow(value);
          if (!host) continue;
          const members = [host, ...childrenOf(host.encounter_creatures_arr).map(asRow).filter((row): row is Row => !!row)];
          if (!members.some(row => ecId(row) === actorId)) continue;
          const applied = applyAppliedFrame(delta, host, false, false);
          paintAppliedTree(host, applied.value as Row);
          for (const row of members) if (ecId(row) === actorId) copies.add(row);
        }
      }
    }
    const aps = Number(encounterCreatureField(caster.encounter_creatures, 'aps') ?? 0) - command.ap.cost;
    const revision = Number(encounterCreatureField(caster.encounter_creatures, 'movement_rev') ?? 0) + (command.ap.cost > 0 ? 1 : 0);
    const pending: PendingEquip = { actorId, rows: plan.rows, aps, revision, dependenciesPending: kind === 'equip' && copies === null };
    pendingEquips.set(operationId, pending);
    patchCaches(plan.rows);
    for (const copy of copies ?? recountEquipped(actorId, plan.rows)) {
      const ec = asRow(copy.encounter_creatures)!;
      applyApsScalar(ec, aps); ec.movement_rev = revision;
      (window as unknown as { ftRenderCasterRowLocal?: (row: Row, paths: readonly string[]) => void }).ftRenderCasterRowLocal?.(copy,
        ['inventory', 'computed.creature.equipped', ...dependencyPaths, 'encounter_creatures.aps', 'encounter_creatures.movement_rev']);
    }
    window.dispatchEvent(new CustomEvent('ft:inventory-equip-truth', { detail: { kind: 'provisional', frame: {
      ...command, encounter_id: encounterId, rows: plan.rows, aps,
    } } }));
    const wire = { ...command, encounter_id: encounterId, rows: plan.rows, aps };
    const applied = inventoryAppliedFrame(wire, [
      ...plan.rows.map(change => ({ table: 'inventory' as const, id: change.inventory_id, before: {}, after: { ...change } })),
      { table: 'encounter_creatures', id: actorId, before: {}, after: { aps, movement_rev: revision } },
    ]);
    if (pushedDelta) { applied.computed_delta = pushedDelta.computed_delta; applied.inputs = pushedDelta.inputs; }
    pushInventoryTruth('janus_inventory_equip', wire, applied);
    queueAppliedSave({ encounterId, actorIds: [actorId], command, confirmed: [{ actorId, rev: revision, aps }],
      send: async () => {
        pending.sent = true;
        const { response, body } = await postCommand(command);
        if (!response || response.status >= 500) throw new Error('Equipment save unavailable');
        if (!response.ok || body?.ok !== true) return { ok: false, status: response.status, error: String(body?.error ?? 'equip refused'), saved: body?.saved };
        // A later saved intent owns each row it changes. Carry an older fallback's
        // deferral forward until these latest slots and dependencies are adopted.
        const savedRows = new Set(pending.rows.map(change => upper(change.inventory_id)));
        for (const [id, prior] of pendingEquips) {
          if (id === operationId) break;
          if (prior.actorId !== actorId) continue;
          const remaining = prior.rows.filter(change => !savedRows.has(upper(change.inventory_id)));
          if (remaining.length === prior.rows.length) continue;
          pending.dependenciesPending ||= prior.dependenciesPending === true;
          prior.rows = remaining;
          if (!remaining.length) pendingEquips.delete(id);
        }
        pending.saved = true;
        if (!pending.dependenciesPending) pendingEquips.delete(operationId);
        return { ok: true };
      },
      refused: async info => {
        pendingEquips.delete(operationId);
        if (info.chained) return;
        const saved = asRow(info.body?.saved);
        if (Array.isArray(saved?.rows)) patchCaches(saved.rows as EquipPlanRow[]);
        await healCaster(actorId);
        const message = info.status === 409 ? 'The equipment changed elsewhere; its current state was restored.' : 'The equipment change was refused: ' + info.error;
        (window as unknown as { displayWarning?: (message: string) => void }).displayWarning?.(message);
      },
    });
    return { ok: true, status: 200, operation_id: operationId, c1: 'base_patch', local: true };
  }
  {
    pushInventoryTruth('janus_inventory_equip', { ...command, encounter_id: encounterId, rows: plan.rows },
      inventoryAppliedFrame({ ...command, encounter_id: encounterId }, timed?.activity.writes ?? []));
    const { response, body } = await postCommand(command);
    if (response === null) return { ok: false, status: 0, error: 'network', message: 'The equip could not be confirmed — check your connection.' };
    if (response.ok && body?.ok === true) {
      // Keep the saved activity row immediately; its relayed delta installs only
      // the affected status clusters. No timer guesses whether to reload an actor.
      if (timed) {
        const saved = asRow(asRow(body.saved)?.status);
        if (saved) {
          const copies = new Set<Row>([casterRow(actorId), ...childrenOf(encounter?.encounter_creatures_arr).map(asRow)]
            .filter((r): r is Row => !!r && upper(asRow(r.encounter_creatures)?.encounter_creature_id) === actorId));
          for (const copy of copies) {
            const statuses = asRow(copy.statuses) ?? (copy.statuses = {} as Row) as Row;
            statuses.target = [...childrenOf(statuses.target).filter(s => upper(asRow(s)?.status_id) !== upper(saved.status_id)), saved];
          }
        }
      }
      return { ok: true, status: response.status, operation_id: command.operation_id, c1: 'base_patch' };
    }

    await healCaster(actorId);
    if (bodyHolder) await healCaster(bodyHolder); // the body's row this page read may be the stale copy
    return { ok: false, status: response.status, error: String(body?.error ?? 'error'), message: typeof body?.message === 'string' ? body.message : undefined };
  }
}

/** N6b (2026-10-01, R-088) / N6c (R-089 / R-101): every equip-family hand-back reason, worded — the legacy swap_hands /
 *  equip_both_hands / unequip_both_hands AND equip_or_swap_inventory endpoints are DELETED; what the page cannot decide is refused here,
 *  never posted (owner rule: no hand-back that makes PHP judge an unported rule — the playtest filters the refusals). */
const HAND_KINDS: ReadonlySet<InventoryEquipKind> = new Set(['swap_hands', 'both_hands', 'unequip_both_hands']);
function handbackWords(kind: InventoryEquipKind, why: string): string {
  const what = HAND_KINDS.has(kind) ? 'Your hands were not changed' : 'That was not equipped';
  switch (why) {
    case 'ap:fast-draw-unknown': return `${what}: this page cannot tell whether Fast Draw applies (a feat with conditions decides it). Reload the page and try again.`;
    case 'rule:colossal-feat': return `${what}: this page cannot tell whether your Colossal weapon training applies (a feat with conditions decides it).`;
    case 'rule:grip-feat': return `${what}: this page cannot tell whether your grip feat applies (a feat with conditions decides it).`;
    case 'no-caster-row': return `${what}: this character could not be loaded on this page. Reload the page and try again.`;
    case 'combat-unknown': return `${what}: this character's encounter could not be loaded on this page. Reload the page and try again.`;
    case 'no-plan': return `${what}: no equipment slot could be found for it.`;
    case 'no-row': case 'no-item': return `${what}: that item is not carried by this character. Reload the page and try again.`;
    default: return `${what} (${why || 'this case is not available here'}). Reload the page and try again.`;
  }
}
function refuseWords(kind: InventoryEquipKind, message: string, why?: string): InventoryEquipAnswer {
  window.dispatchEvent(new CustomEvent('ft:equip-truth-refused-words', { detail: { message, why: why ?? null, kind } }));
  return { ok: false, status: 0, error: 'refused', message };
}

/** The three hand buttons (swap hands / both hands / one hand) — client truth only (R-088). */
export async function equipHands(kind: 'swap_hands' | 'both_hands' | 'unequip_both_hands'): Promise<void> {
  return equipOrLegacy(kind, '');
}

/** N6f: the move lane's options (MV5 `InventoryMoveOptions`, move-handlers.ts — mirrored here, not imported: that module's page globals are
 *  not this bundle's). `requiredDecision` binds the move to the decision this chain checked (a re-decided merge / AP block refuses);
 *  `splitInPlace` splits one off a stack the actor holds directly; `apLeg: 'fast-draw'` is the 0-AP draw leg. */
interface MoveOptions { requiredDecision?: ClientMoveDecision; apLeg?: 'standard' | 'fast-draw'; splitInPlace?: boolean }
/** The move receipt as this chain reads it: the landed row, and the AP the move charged (`ap_charged` — the posted decision's own AP). */
interface MoveReceiptAnswer {
  move?: { destination?: { inventory_id?: string; item_qty_after?: number; deleted_after?: boolean; possess_id?: string; created?: boolean } };
  ap_charged?: { cost: number; in_combat: boolean; before: number | null; after: number | null };
}
/** A move refused BEFORE anything was written (the page's rule, a changed required decision, a pre-write C1 refusal). */
interface MoveRefusalAnswer { schema: 'ft3d.inventory-move-refusal.v1'; ok: false; refused: string; message: string }
type MoveWithReceipt = (inventoryId: string, qty: number, fromId: string | null, toId: string, moveType: string, showLog: number, x?: null, z?: null,
  moved?: number, clientInstance?: string, sequence?: number, elevation?: null, quiet?: boolean, mutationId?: string, mutationRetry?: boolean,
  options?: MoveOptions | null) => Promise<MoveReceiptAnswer | MoveRefusalAnswer | null>;
const isMoveRefusal = (a: unknown): a is MoveRefusalAnswer => asRow(a)?.schema === 'ft3d.inventory-move-refusal.v1';

/**
 * N6f — FAST DRAW FROM A BAG: the owner's ruling switch (one line, no flag / env). `false` = the draw out of the bag is an ordinary move
 * leg and costs its 1 AP, so a Fast Draw weapon taken from a bag costs 1 AP in all (N6c's worker pick, OWNER TO REVIEW). `true` = the
 * move lane's 0-AP Fast Draw leg (MV5 `apLeg: 'fast-draw'`) carries it out free whenever the equip rules find Fast Draw for the weapon,
 * so the chain costs the legacy 0 AP.
 */
const FAST_DRAW_FROM_BAG_FREE = false;

/**
 * N6d (2026-10-02, coordinator Codex review N1b) / N6e (round 5 T1): a bag move whose answer never came, per actor + source row. PROVENANCE
 * is the server's receipt for THAT move's mutation id (never "a new row of the same item"): every equip of the actor that could involve it
 * (equipInventory step 0 — the bag chain AND a direct equip, Codex round 5 P2) first asks the move endpoint, receipt-only, what became of
 * that id (ft_inventory_move_idempotency_receipt_only): `receipt` → the destination row IS the item that came out — it is equipped with the
 * move's AP credit, and the record goes only once that equip SAVED (round 5 P2: never before); `none` → nothing landed and the id is spent
 * server-side (a delayed copy of the lost request can no longer move a second item) → the record goes and a new click moves under a NEW id;
 * `pending` / `unknown` → words, record kept. The server keeps a receipt 600 s from its own write (after our send; N6e raised it from
 * 90 s), so the page asks only within BAG_MOVE_TRUST_MS of the send; past it nothing can be proven — a click it could involve is refused
 * once with words and the record dropped (worker pick, owner to review); any other record past it is dropped silently. A record whose
 * landed row is known lapses then too (its AP credit belongs to the moment of the move). The bag chain's OWN answered move keeps its record
 * (landed row known) until that equip saved, so a failed equip leaves the credit for the next try (Codex N6e review P2).
 */
interface UnconfirmedBagMove { mutationId: string; at: number; actorId: string; sourceId: string; qtyBefore: number; itemId: string; heldBefore: Set<string>;
  credit: number; fat: Row; liftedInventory: Row; landedId?: string;
  /** N6f: 'bag' = taken out of a bag / sheath; 'stack' = split off a stack the actor holds directly (the split-equip). Same settlement. */
  origin: MoveOrigin }
type MoveOrigin = 'bag' | 'stack';
/** The player-facing phrase for what the chain's move did ("taken out of its bag" / "split off its stack"). */
const MOVED_WORDS: Record<MoveOrigin, string> = { bag: 'taken out of its bag', stack: 'split off its stack' };
const BAG_MOVE_TRUST_MS = 540_000; // FT_INVENTORY_MOVE_IDEMPOTENCY_RECEIPT_SECONDS (600 s) minus a minute for the requests' latency + the probe's own wait
/**
 * MV2 (2026-10-02, coordinator Codex round 13 P2): the records SURVIVE A RELOAD. An in-memory Map lost the mutation id on a reload, so a
 * retry on the original stack after a lost answer bypassed the settlement and took a SECOND item. Each record (keyed actor + source row) is
 * written through to sessionStorage (this tab; try/catch — storage blocked / full = the old in-memory behaviour) and read back when the
 * module loads, so step 0 of the next equip asks the server's receipt for that id exactly as it would have without the reload. A record
 * older than twice the trust window is not restored (step 0 would only drop it — the expired-words refusal belongs to the first window).
 * Mutating a record in place (landedId / credit) must call `persist()`.
 */
const BAG_MOVE_STORE_KEY = 'ft:equip:unconfirmed-bag-moves:v1';
class PersistedBagMoves extends Map<string, UnconfirmedBagMove> {
  private hydrated = false;
  constructor() {
    super();
    try {
      const raw = typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(BAG_MOVE_STORE_KEY);
      const list = raw ? JSON.parse(raw) as unknown : null;
      for (const entry of Array.isArray(list) ? list : []) {
        const [key, rec] = Array.isArray(entry) ? entry as [unknown, unknown] : [null, null];
        const r = asRow(rec);
        if (typeof key !== 'string' || !r || typeof r.mutationId !== 'string' || typeof r.actorId !== 'string' || typeof r.sourceId !== 'string'
          || !Number.isFinite(Number(r.at)) || Date.now() - Number(r.at) > 2 * BAG_MOVE_TRUST_MS) continue;
        super.set(key, { ...(r as unknown as UnconfirmedBagMove), at: Number(r.at), heldBefore: new Set(Array.isArray(r.heldBefore) ? (r.heldBefore as unknown[]).map(String) : []),
          origin: r.origin === 'stack' ? 'stack' : 'bag' });
      }
    } catch { /* unreadable storage: start empty */ }
    this.hydrated = true;
  }
  override set(key: string, value: UnconfirmedBagMove): this { super.set(key, value); this.persist(); return this; }
  override delete(key: string): boolean { const had = super.delete(key); this.persist(); return had; }
  override clear(): void { super.clear(); this.persist(); }
  persist(): void {
    if (!this.hydrated) return;
    try {
      if (typeof sessionStorage === 'undefined') return;
      if (this.size === 0) { sessionStorage.removeItem(BAG_MOVE_STORE_KEY); return; }
      sessionStorage.setItem(BAG_MOVE_STORE_KEY, JSON.stringify([...this.entries()].map(([k, r]) => [k, { ...r, heldBefore: [...r.heldBefore] }])));
    } catch { /* storage full / blocked: the in-memory record still guards this page life */ }
  }
}
const unconfirmedBagMoves = new PersistedBagMoves();
const MOVE_ENDPOINT = '/options/tools/inventory/ft3d/move_draggable_inventory_inc.php';
type ReceiptProbe = { outcome: 'receipt'; landedId: string; qty: number; deleted: boolean; holder: string } | { outcome: 'none' | 'pending' | 'unknown' };
/** Ask the move endpoint, receipt-only, what became of an earlier move under `mutationId` (nothing moves). */
async function probeMoveReceipt(mutationId: string): Promise<ReceiptProbe> {
  try {
    const res = await fetch(MOVE_ENDPOINT, { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ mutation_id: mutationId, receipt_only: 1 }), signal: AbortSignal.timeout(12000) });
    const body = asRow(await res.json().catch(() => null));
    if (!res.ok || body?.schema !== 'ft3d.inventory-move-receipt-probe.v1' || body.ok !== true) return { outcome: 'unknown' };
    if (body.outcome === 'none' || body.outcome === 'pending') return { outcome: body.outcome };
    const dest = asRow(asRow(asRow(body.receipt)?.move)?.destination);
    if (body.outcome !== 'receipt' || !dest?.inventory_id) return { outcome: 'unknown' };
    return { outcome: 'receipt', landedId: upper(dest.inventory_id), qty: Number(dest.item_qty_after ?? 1), deleted: !!dest.deleted_after, holder: upper(dest.possess_id) };
  } catch { return { outcome: 'unknown' }; }
}
/** May `clicked` be the row an unconfirmed move of this record landed as? Its source row itself (a single item keeps its id), or a row the
 *  actor holds directly now that it did not before, of the same item, single and unequipped. Only such a click consults the record (a click
 *  on anything else is not the record's business). */
function couldBeLanded(rec: UnconfirmedBagMove, clicked: string, live: Row | null): boolean {
  if (clicked === rec.sourceId || clicked === rec.landedId) return true;
  return directRows(live, rec.actorId).some(r => r.id === clicked && !rec.heldBefore.has(r.id) && r.itemId === rec.itemId && r.qty === 1 && !r.equipped);
}
const UNSURE_WORDS: Record<MoveOrigin, string> = {
  bag: 'That was not equipped: an earlier attempt to take it out of its bag was never confirmed, and one may already be out. Try again in a moment.',
  stack: 'That was not equipped: an earlier attempt to split one off that stack was never confirmed, and one may already be split off. Try again in a moment.',
};
const EXPIRED_WORDS: Record<MoveOrigin, string> = {
  bag: 'That was not equipped: an earlier attempt to take one out of its bag was never confirmed and can no longer be checked. Look in your inventory — if one is out, equip that one; otherwise equip again to take one out.',
  stack: 'That was not equipped: an earlier attempt to split one off that stack was never confirmed and can no longer be checked. Look in your inventory — if a single one is there, equip that one; otherwise equip again to split one off.',
};
/**
 * Step 0 of every equip (equipInventory, under the actor's lock): settle the unconfirmed bag moves the click could involve. Answers an
 * InventoryEquipAnswer when the click is decided here (refused, or the landed row equipped), else null (carry on as a normal equip).
 */
async function settleUnconfirmedMoves(actorId: string, clicked: string, token: symbol): Promise<InventoryEquipAnswer | null> {
  const mine = [...unconfirmedBagMoves.entries()].filter(([, r]) => r.actorId === actorId);
  if (!mine.length) return null;
  await healCaster(actorId);
  let expiredHere: MoveOrigin | null = null;
  for (const [key, rec] of mine) {
    const relevant = couldBeLanded(rec, clicked, casterRow(actorId));
    if (Date.now() - rec.at > BAG_MOVE_TRUST_MS) {
      // Past what the server can prove (Codex N6e review P3: also when the click is unrelated — a record must not live for the page's
      // lifetime). A click it could involve is refused once; its landed row, if known, is a plain equip from here (the credit lapsed).
      unconfirmedBagMoves.delete(key);
      if (relevant && !rec.landedId) expiredHere = rec.origin;
      continue;
    }
    if (!relevant) continue;
    if (!rec.landedId) {
      const probe = await probeMoveReceipt(rec.mutationId);
      if (probe.outcome === 'none') { unconfirmedBagMoves.delete(key); continue; } // nothing landed under that id (now spent): a fresh equip
      if (probe.outcome !== 'receipt') return refuseWords('equip', UNSURE_WORDS[rec.origin], 'move-unconfirmed'); // pending / unknown: kept
      if (probe.deleted || probe.qty !== 1 || (probe.holder && probe.holder !== actorId) || (rec.origin === 'stack' && probe.landedId === rec.sourceId)) {
        unconfirmedBagMoves.delete(key);
        return refuseWords('equip', `That was ${MOVED_WORDS[rec.origin]} earlier but not equipped: it joined a stack you carry. Drag one onto an equipment slot.`, 'landed-in-stack');
      }
      rec.landedId = probe.landedId;
      unconfirmedBagMoves.persist(); // MV2: an in-place change survives a reload too
      // The receipt may name a move that committed AFTER the heal above (Codex N6e review P2): re-read before judging the row's absence.
      await healCaster(actorId);
    }
    // The landed row is the item that came out: equip it, with the move's AP credit. The record goes only once that equip SAVED — or when
    // the row is (on a fresh read) no longer a single unequipped row held directly: it left, nothing is left to credit.
    const landedId = rec.landedId!;
    const heldSingle = (): boolean => directRows(casterRow(actorId), actorId).some(r => r.id === landedId && r.qty === 1 && !r.equipped);
    if (!heldSingle()) {
      await healCaster(actorId);
      if (!heldSingle()) {
        unconfirmedBagMoves.delete(key);
        // Codex N6e review round 2 P2: an equip whose ANSWER was lost may have saved — the landed row is then equipped. A click on its source
        // (or on it) is that equip, done: settled here, never a fall-through that takes a second item out.
        const nowEquipped = directRows(casterRow(actorId), actorId).some(r => r.id === landedId && r.equipped);
        if (nowEquipped && (clicked === rec.sourceId || clicked === landedId)) return { ok: true, status: 200, error: 'already-equipped', message: `That one was already ${MOVED_WORDS[rec.origin]} and equipped.` };
        continue;
      }
    }
    if (clicked !== landedId && clicked !== rec.sourceId) continue; // another candidate row: the record waits for its own row
    const answer = await equipLanded(actorId, landedId, rec.credit, token, rec.fat, rec.liftedInventory, rec.origin);
    settleEquipAnswer(key, answer);
    return answer;
  }
  return expiredHere ? refuseWords('equip', EXPIRED_WORDS[expiredHere], 'move-unprovable') : null;
}
/** The quantity of `inventoryId` anywhere in the caster's carried tree, or null when the row is not there. */
function carriedQty(caster: Row | null, inventoryId: string): number | null {
  let hit: number | null = null;
  const walk = (nodes: unknown, depth: number): void => {
    if (hit !== null || depth > 8 || !Array.isArray(nodes)) return;
    for (const n of nodes) {
      const r = asRow(n); if (!r) continue;
      const b = asRow(r.inventory);
      if (upper(b?.inventory_id) === inventoryId) { const q = Number(Array.isArray(b?.item_qty) ? (b!.item_qty as unknown[])[0] : b?.item_qty ?? 1); hit = Number.isFinite(q) ? q : 1; return; }
      walk(r.inventory_in_container, depth + 1);
    }
  };
  walk(caster?.inventory, 0);
  return hit;
}

/** Wait (bounded) until the page's caster row holds `inventoryId` as a single, directly held, unequipped row — the move receipt's
 *  projection usually lands before the move answer resolves; a slow page heals the caster once. */
async function awaitHeldSingle(actorId: string, inventoryId: string): Promise<boolean> {
  const ready = (): boolean => { const c = casterRow(actorId); return !!c && equipItemEligible(c, inventoryId, actorId); };
  for (let i = 0; i < 20 && !ready(); i++) await new Promise<void>(r => setTimeout(r, 150));
  if (!ready()) await healCaster(actorId);
  return ready();
}

/**
 * N6c (2026-10-01, R-089 / R-101): the action-bar EQUIP seam (equip_or_swap_inventory_inc.php is DELETED). A single row held directly
 * commits through the envelope at once. A row in a bag / sheath (or one item of a stack in a bag) is a MOVE-THEN-EQUIP chain:
 *   1. a DRY plan of the equip against the caster as if the item were already in hand — every rule and the whole AP judged before anything
 *      moves (a refusal shows the rule's words, nothing is spent);
 *   2. the move lane's own decision (move-decision.ts, read-only) must not merge the item into a carried stack (it could not be equipped);
 *   3. window.ftMoveInventoryWithReceipt takes ONE item out to the character (the move lane's endpoint, its AP leg);
 *   4. the envelope equips the row the receipt names (a stack's split row is a NEW id) with the move's AP as credit, so the chain costs
 *      what the one legacy equip did (except a Fast Draw weapon from a bag: the move's 1 AP — legacy charged 0; owner to review — the
 *      FAST_DRAW_FROM_BAG_FREE switch flips it onto the move lane's 0-AP leg).
 * N6f (2026-10-02): the move is BOUND to the decision step 2 checked (MV5 `requiredDecision` — a re-decided merge / AP block refuses before
 * anything is written), and the equip's credit is the AP the move CHARGED (`receipt.ap_charged`), never a projection.
 * N6f — a STACK HELD DIRECTLY by the character is a SPLIT-EQUIP (retires N6c's `stack-held-directly` refusal): the move lane's split in place
 * (MV5 `splitInPlace`, 0 AP — its worker pick) makes a NEW row of one under the same holder, and the envelope equips that row (it pays the
 * whole equip). Same chain, same lock, same lost-answer settlement. Every other hand-back is refused with words.
 */
export async function equipInventory(inventoryIdRaw: string, actorIdRaw?: string | null): Promise<InventoryEquipAnswer> {
  const w = window as unknown as { ftGetActiveCasterId?: () => string; ftMoveInventoryWithReceipt?: MoveWithReceipt; FateTide?: { user?: { isGM?: boolean } } };
  const inventoryId = upper(inventoryIdRaw);
  const actorId = upper(actorIdRaw || w.ftGetActiveCasterId?.() || '');
  // N6e (coordinator Codex review round 5 P2): an unconfirmed bag move of this actor is consulted on EVERY equip path — a direct equip of
  // the row that move landed as must carry its AP credit (and settle its record), not pay the equip again.
  if ([...unconfirmedBagMoves.values()].some(r => r.actorId === actorId)) {
    if (inflight.has(actorId)) return { ok: false, status: 0, error: 'pending', message: 'Your last equip is still being saved.' };
    const settleToken = Symbol('equip-settle');
    inflight.add(actorId);
    chainTokens.set(actorId, settleToken);
    try {
      const settled = await settleUnconfirmedMoves(actorId, inventoryId, settleToken);
      if (settled) return settled;
    } finally {
      chainTokens.delete(actorId);
      inflight.delete(actorId);
    }
  }
  const first = await commitInventoryEquip({ kind: 'equip', actorId, inventoryId });
  if (!first.handback) return first;
  if (first.error !== 'not-a-single-held-row') return refuseWords('equip', handbackWords('equip', String(first.error ?? '')), String(first.error ?? ''));
  const caster = casterRow(actorId);
  const row = caster ? carriedRows(caster).find(r => r.inventory_id === inventoryId) : undefined;
  if (!caster || !row) return refuseWords('equip', handbackWords('equip', 'no-row'), 'no-row');
  if (row.equipped !== '') return refuseWords('equip', 'That item is already equipped.', 'already-equipped');
  // N6f: held directly (a stack — a single held row equipped above) → split one off and equip it; else it sits in a bag / sheath.
  const origin: MoveOrigin = row.possess_id === actorId ? 'stack' : 'bag';
  if (actorId !== upper(w.ftGetActiveCasterId?.() ?? '')) return refuseWords('equip', origin === 'stack' ? 'Select this character first, then equip from its stack.' : 'Select this character first, then equip from its bag.', 'not-active-caster');
  if (typeof w.ftMoveInventoryWithReceipt !== 'function') return refuseWords('equip', origin === 'stack' ? 'That was not equipped: this page cannot split one off that stack. Reload the page and try again.' : 'That was not equipped: this page cannot take it out of its bag. Reload the page and try again.', 'no-move-seam');
  // ONE actor-scoped lock across the dry plan, the move and the equip (Codex N6c review P2 #3): no other equip of this actor can start
  // mid-chain, and the chain's own final commit runs inside it (its token).
  if (inflight.has(actorId)) return { ok: false, status: 0, error: 'pending', message: 'Your last equip is still being saved.' };
  const token = Symbol('equip-chain');
  inflight.add(actorId);
  chainTokens.set(actorId, token);
  try {
    return await equipViaMove(w, actorId, inventoryId, caster, row, token, origin);
  } finally {
    chainTokens.delete(actorId);
    inflight.delete(actorId);
  }
}

/** Project the AP a move CHARGED (N6f: its receipt's `ap_charged` before → after) onto the page's copies of the actor: only where a copy
 *  still shows that `before` — a caster update that already landed is newer truth (Codex N6c review P2 #2). */
function projectMoveAps(actorId: string, before: number, after: number): void {
  const ft = ftCaches();
  const copies: Row[] = [];
  if (ft?.casterCache instanceof Map) for (const [k, v] of ft.casterCache.entries()) if (upper(k) === actorId && asRow(v)) copies.push(v as Row);
  if (ft?.encounterCache instanceof Map) for (const enc of ft.encounterCache.values()) {
    for (const m of (asRow(enc)?.encounter_creatures_arr as unknown[] | undefined) ?? []) if (upper(asRow(asRow(m)?.encounter_creatures)?.encounter_creature_id) === actorId) copies.push(m as Row);
  }
  for (const c of copies) {
    const ec = asRow(c.encounter_creatures);
    const now = Number(Array.isArray(ec?.aps) ? (ec!.aps as unknown[])[0] : ec?.aps);
    if (ec && now === before) ec.aps = after;
  }
}

/**
 * The move-then-equip chain (equipInventory): `origin` 'bag' takes ONE item out of a bag / sheath to the actor; 'stack' (N6f split-equip)
 * splits ONE off a stack the actor holds directly into a new row. Then the envelope equips the row the receipt names.
 */
async function equipViaMove(w: { ftMoveInventoryWithReceipt?: MoveWithReceipt; FateTide?: { user?: { isGM?: boolean } } }, actorId: string, inventoryId: string,
  caster: Row, row: { possess_id: string }, token: symbol, origin: MoveOrigin): Promise<InventoryEquipAnswer> {
  // (0. An earlier attempt on this row whose move answer never came is settled BEFORE this — equipInventory's settleUnconfirmedMoves, from
  //     the server's receipt for that move's mutation id; reaching here means nothing of it landed, so this move gets a NEW id.)
  const moveKey = actorId + '|' + inventoryId;
  const moved = MOVED_WORDS[origin];
  // 1. the dry plan — the caster as if ONE of the item were already held directly (the row lifted out of its bag — or off its stack — at
  //    quantity 1; the rest of a split stack plays no part in any equip rule).
  const view = inventoryDecisionView(new Map([[actorId, caster]]));
  view.equipment(actorId, slots(), inventoryId);
  const liftedRow = view.item(inventoryId);
  const snapshot = view.held(actorId, inventoryId);
  if (!snapshot || !liftedRow) return refuseWords('equip', handbackWords('equip', 'no-row'), 'no-row');
  // The FAT row (its layers resolved while its own id still resolves on this page) — grafted onto the landed row for the final plan.
  const encNow = (() => { const c = ftCaches()?.encounterCache; const id = upper(asRow(caster.encounter_creatures)?.encounter_id); return c instanceof Map ? asRow(c.get(id) ?? c.get(id.toLowerCase())) : null; })();
  const liftedFat = createEquipPageReaders(caster, encNow).fat(liftedRow);
  const dry = await commitInventoryEquip({ kind: 'equip', actorId, inventoryId, dryRun: true, casterOverride: snapshot });
  if (!dry.ok) return dry.handback ? refuseWords('equip', handbackWords('equip', String(dry.error ?? '')), String(dry.error ?? ''))
    : refuseWords('equip', dry.message || 'That was not equipped.', String(dry.error ?? ''));
  // 2. the move lane's decision, read-only: a merge into a carried stack (or its own refusal) stops the chain before anything moves.
  //    A bag draw of a weapon the equip rules give Fast Draw rides the move lane's 0-AP leg only when the owner's switch says so
  //    (FAST_DRAW_FROM_BAG_FREE); a split in place is always the move lane's free split (the equip pays).
  const encounterId = upper(asRow(caster.encounter_creatures)?.encounter_id);
  const snapRead = createEquipPageReaders(snapshot, encNow);
  const fastDrawLeg = origin === 'bag' && FAST_DRAW_FROM_BAG_FREE && snapRead.itemType(liftedFat) === 'weapon' && snapRead.provides(snapshot, 'fast_draw') === true;
  const verdict = decideInventoryMove({ casterId: actorId, inventoryId, qty: 1, moveToPossessId: actorId, moveType: 'move_inventory', encounterId,
    isGm: w.FateTide?.user?.isGM === true, editorSurface: false, ...(origin === 'stack' ? { splitInPlace: true } : fastDrawLeg ? { apLeg: 'fast-draw' as const } : {}) });
  if ('refuse' in verdict) return refuseWords('equip', verdict.refuse, 'move-refused');
  // Only an explicit decision that lands it as its OWN row (target null) may move (Codex N6c review P2 #1): an undecidable move (an
  // ambiguous stack, a normalization the page cannot judge, …) could merge it into a carried stack after AP was spent.
  if (!('decision' in verdict)) {
    return refuseWords('equip', `That was not equipped: this page cannot tell where ${origin === 'stack' ? 'splitting one off would put it' : 'taking it out of its bag would put it'} (${'handback' in verdict ? verdict.handback : 'doff'}). Drag it onto an equipment slot instead.`, 'move-undecidable');
  }
  if (verdict.decision.target !== null) {
    return refuseWords('equip', 'That was not equipped: taking it out would join it to a stack you carry. Drag it onto an equipment slot instead.', 'would-merge');
  }
  const decision = verdict.decision;
  // The decided AP leg in combat (moveApCost: 1, a shield 2; the Fast Draw / split legs 0) — the record's credit until the receipt says
  // what the move actually charged (a lost answer settles with this one: the endpoint charges exactly the posted decision's AP).
  const decidedCredit = decision.ap.in_combat ? decision.ap.cost : 0;
  // 3. move ONE (the move lane's endpoint persists the decision this page posted; the receipt names the row that landed). BOUND to the
  //    decision above (N6f / MV5 `requiredDecision`): a move that would re-decide (after its reach check, a stale-claim retry) refuses.
  //    N6d / N6e: remembered until answered, so a lost answer is settled from the server's receipt for THIS id (equipInventory step 0) on the
  //    next equip instead of moving a second item. The move's own transport retry re-sends this same id (the endpoint replays its receipt).
  const mutationId = (crypto.randomUUID?.() ?? '').toUpperCase();
  if (!mutationId) return refuseWords('equip', handbackWords('equip', 'no-uuid'), 'no-uuid');
  const sourceBucket = asRow(liftedRow.inventory) ?? {};
  unconfirmedBagMoves.set(moveKey, { mutationId, at: Date.now(), actorId, sourceId: inventoryId, qtyBefore: carriedQty(caster, inventoryId) ?? 1,
    itemId: rowItemId(liftedRow), heldBefore: new Set(directRows(caster, actorId).map(r => r.id)), credit: decidedCredit, fat: liftedFat, liftedInventory: sourceBucket, origin });
  // Never evicted by size (N6d review round 2 P2): a record lives until its move is answered, settled, or past BAG_MOVE_TRUST_MS on a click.
  const options: MoveOptions = { requiredDecision: decision, ...(origin === 'stack' ? { splitInPlace: true } : fastDrawLeg ? { apLeg: 'fast-draw' as const } : {}) };
  // Codex N6f review P2: the move lane decides as the page's ACTIVE caster, read synchronously when the call starts — the active caster may
  // have changed during the dry run's awaits. Checked in the SAME tick as the call, so the move is decided for this actor or not at all.
  const activeNow = upper((window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId?.() ?? '');
  if (activeNow !== actorId) {
    unconfirmedBagMoves.delete(moveKey);
    return refuseWords('equip', 'That was not equipped: another character was selected meanwhile. Select this character, then equip it again.', 'actor-switched');
  }
  let answer: Awaited<ReturnType<MoveWithReceipt>> = null;
  try { answer = await w.ftMoveInventoryWithReceipt!(inventoryId, 1, row.possess_id, actorId, 'move_inventory', 1, null, null, 0, '', 0, null, true, mutationId, false, options); } catch { answer = null; }
  if (isMoveRefusal(answer)) {
    // Refused BEFORE anything was written (the move lane's rule, a re-decided move, a pre-write C1 refusal): nothing moved, nothing spent.
    unconfirmedBagMoves.delete(moveKey);
    return refuseWords('equip', `That was not equipped: ${String(answer.message || 'the move was refused').replace(/^That was not moved: /, '').replace(/^That move was not made: /, '')}`, 'move-' + String(answer.refused || 'refused'));
  }
  const receipt = answer;
  const landed = receipt?.move?.destination;
  if (!landed?.inventory_id) return refuseWords('equip', `That was not equipped: ${origin === 'stack' ? 'splitting one off that stack' : 'taking it out of its bag'} was not confirmed. Check your inventory — equipping it again is safe (it will not ${origin === 'stack' ? 'split off' : 'take out'} a second one).`, 'move-failed');
  const landedId = upper(landed.inventory_id);
  if (landed.deleted_after || Number(landed.item_qty_after ?? 1) !== 1 || upper(landed.possess_id ?? actorId) !== actorId || (origin === 'stack' && landedId === inventoryId)) {
    unconfirmedBagMoves.delete(moveKey); // answered: nothing of it can be equipped
    return refuseWords('equip', `That was ${moved} but not equipped: it joined a stack you carry. Drag one onto an equipment slot.`, 'landed-in-stack');
  }
  // N6f (MV5 ask b): the AP the move CHARGED — the receipt's `ap_charged` (the posted decision's AP, which the endpoint charges exactly after
  // its AP CAS) — is the equip leg's credit, never a projection; it also corrects the record so a later settlement credits the same.
  const charged = receipt?.ap_charged;
  const credit = charged ? (charged.in_combat ? Math.max(0, Math.trunc(charged.cost)) : 0) : decidedCredit;
  // Answered: the record now names the landed row and stays until that equip SAVED (Codex N6e review P2) — a failed equip leaves the move's
  // AP credit for the next equip of that row (settleUnconfirmedMoves).
  const rec = unconfirmedBagMoves.get(moveKey);
  if (rec) { rec.landedId = landedId; rec.credit = credit; unconfirmedBagMoves.persist(); }
  // The move saved that AP (CAS on `before`) — project it onto the page's copies so the equip's `before.aps` is the saved one.
  const apBefore = charged ? charged.before : decision.ap.before;
  if (credit > 0 && apBefore !== null) projectMoveAps(actorId, apBefore, charged?.after ?? apBefore - credit);
  const equipped = await equipLanded(actorId, landedId, credit, token, liftedFat, sourceBucket, origin);
  settleEquipAnswer(moveKey, equipped);
  return equipped;
}

/** After the chain's equip of a landed row: saved → the record goes; an equip whose ANSWER was lost may have saved and spent the move's credit
 *  (Codex N6f review P2: the row could be unequipped elsewhere and equipped again with the same credit) → the record keeps guarding the
 *  source against a second move, but its credit is FORFEITED (worker pick, owner to review: a lost equip answer can over-charge the move's AP
 *  once, never discount a second equip). Any other failure keeps the credit for the next equip of that row. */
function settleEquipAnswer(moveKey: string, answer: InventoryEquipAnswer): void {
  if (answer.ok) { unconfirmedBagMoves.delete(moveKey); return; }
  const rec = unconfirmedBagMoves.get(moveKey);
  if (rec && answer.status === 0 && answer.error === 'network') { rec.credit = 0; unconfirmedBagMoves.persist(); }
}

/** N6d: the top-level rows the actor holds directly (id, item, quantity, equipped) — the reconcile step's before / after comparison. */
function rowItemId(r: Row): string { const b = asRow(r.inventory); return upper(b?.inventory_item_id ?? asRow(r.items)?.item_id ?? ''); }
function directRows(caster: Row | null, actorId: string): Array<{ id: string; itemId: string; qty: number; equipped: boolean }> {
  const out: Array<{ id: string; itemId: string; qty: number; equipped: boolean }> = [];
  for (const n of Array.isArray(caster?.inventory) ? caster!.inventory as unknown[] : []) {
    const r = asRow(n); const b = asRow(r?.inventory);
    if (!r || !b || upper(b.possess_id) !== actorId) continue;
    const q = Number(Array.isArray(b.item_qty) ? (b.item_qty as unknown[])[0] : b.item_qty ?? 1);
    out.push({ id: upper(b.inventory_id), itemId: rowItemId(r), qty: Number.isFinite(q) ? q : 1, equipped: nonEmptyLayer(b.equipped) });
  }
  return out;
}

/** Step 4 of the chain: equip the row that landed (a split's NEW id), the move's charged AP credited, inside the chain's lock. */
async function equipLanded(actorId: string, landedId: string, credit: number, token: symbol, liftedFat: Row, liftedInventory: Row,
  origin: MoveOrigin = 'bag'): Promise<InventoryEquipAnswer> {
  const moved = MOVED_WORDS[origin];
  if (!await awaitHeldSingle(actorId, landedId)) return refuseWords('equip', `That was ${moved} but not equipped: this page has not seen it arrive yet. Equip it again.`, 'landed-not-seen');
  // The page's copy of the landed row may be the receipt's compact projection (no layers / no size-fit passives — smoke 2026-10-02: the
  // dry plan passed and the real one refused the size): plan against the live caster with that row's compiled data grafted from the row
  // as it was BEFORE the move (the same item; its inventory bucket — id, holder, quantity 1, unequipped — from the landed row).
  const grafted = (): Row | undefined => {
    const live = casterRow(actorId);
    if (!live) return undefined;
    const view = inventoryDecisionView(new Map([[actorId, live]]));
    if (!view.item(landedId)) return undefined;
    view.replaceItem(landedId, landedRow => ({ ...liftedFat, inventory: { ...liftedInventory, ...asRow(landedRow.inventory), inventory_id: landedId,
      possess_id: actorId, item_qty: 1, equipped: '' }, inventory_in_container: landedRow.inventory_in_container ?? liftedFat.inventory_in_container }));
    return view.members.get(actorId);
  };
  let answer = await commitInventoryEquip({ kind: 'equip', actorId, inventoryId: landedId, apCredit: credit, chainToken: token, casterOverride: grafted() });
  // A plain 409 `stale` (nothing written; the commit healed the caster — e.g. a caster update raced the AP projection) is retried ONCE from
  // the healed row, so the item does not stay moved-but-unequipped. N6f: the credit both attempts carry is the AP the move CHARGED (its
  // receipt's `ap_charged`), never the outer estimate (Round 3 N1b / Round 5 (e)).
  if (!answer.ok && !answer.handback && answer.status === 409 && answer.error === 'stale') {
    answer = await commitInventoryEquip({ kind: 'equip', actorId, inventoryId: landedId, apCredit: credit, chainToken: token, casterOverride: grafted() });
  }
  if (answer.handback) return refuseWords('equip', `That was ${moved} but not equipped — ` + handbackWords('equip', String(answer.error ?? '')).replace(/^That was not equipped: /, ''), String(answer.error ?? ''));
  // N6d (coordinator Codex review N1b): ANY failure after the move (both equip attempts 409 stale, a refusal, a lost answer) says that the
  // item is OUT of its bag (or off its stack) now — the plain failure ("Your equipment changed elsewhere") read as if nothing had happened.
  // A lost equip answer is UNCERTAIN (the equip may have saved): it says so instead of "not equipped" (N6d review P2).
  if (!answer.ok && answer.status === 0 && answer.error === 'network') {
    return { ...answer, message: `That was ${moved}, but equipping it was not confirmed. Check your inventory before equipping it again.` };
  }
  if (!answer.ok) {
    const why = answer.message || (answer.status === 409 ? 'your equipment changed elsewhere' : 'the equip could not be saved');
    return { ...answer, message: `That was ${moved} but not equipped (${why.replace(/[.!\s]+$/, '').replace(/^\w/, c => c.toLowerCase())}). It is in your inventory now — equip it from there.` };
  }
  return answer;
}

/**
 * The PC sheet's buttons: commit through the envelope. Nothing posts a legacy endpoint (N6b R-088 — the hand endpoints; N6c R-089 /
 * R-101 — equip_or_swap_inventory_inc.php): a hand-back is refused with words. `legacyUrl` / `legacyData` are ignored (kept for callers).
 * A refusal shows its warning (the caster cache was already rehydrated); either way the sheet refreshes.
 */
export async function equipOrLegacy(kind: EquipCommitInput['kind'], _legacyUrl = '', inventoryId = '', _legacyData: Row = {}): Promise<void> {
  const w = window as unknown as { refreshCasterSheet?: (id: string) => unknown; ftGetActiveCasterId?: () => string; displayWarning?: (m: string) => void };
  let answer = kind === 'equip' ? await equipInventory(inventoryId) : await commitInventoryEquip({ kind, inventoryId });
  if (answer.handback) answer = refuseWords(kind, handbackWords(kind, String(answer.error ?? '')), String(answer.error ?? ''));
  const warn = document.getElementById('div_sidelog_warning');
  if (warn) warn.innerHTML = '';
  if (!answer.ok) {
    const text = answer.message || (answer.status === 409 ? 'Your equipment changed elsewhere — try again.'
      : answer.status === 403 ? 'You cannot equip that.' : 'The equip could not be saved.');
    w.displayWarning?.(text);
  }
  const caster = w.ftGetActiveCasterId?.();
  if (caster && !answer.local) await w.refreshCasterSheet?.(caster);
}

/** N6e (coordinator Codex review round 5 P2 — "every equip path"): the window seam. A plain `equip` of an actor that has an unconfirmed bag
 *  move goes through equipInventory, whose step 0 settles that move first (its AP credit, its record); everything else is commitInventoryEquip. */
async function commitInventoryEquipSeam(input: EquipCommitInput): Promise<InventoryEquipAnswer> {
  const ownerId = upper(input.actorId || (window as unknown as { ftGetActiveCasterId?: () => string }).ftGetActiveCasterId?.() || '');
  input = { ...input, actorId: ownerId };
  await window.ftWaitForInventoryBase?.(ownerId);
  if (window.ftRequireInventoryBase && !window.ftRequireInventoryBase(ownerId)) {
    return { ok: false, status: 422, error: 'unported:inventory-base-unavailable' };
  }
  if (input.kind === 'equip' && !input.dryRun && !input.chainToken) {
    if (ownerId && [...unconfirmedBagMoves.values()].some(r => r.actorId === ownerId)) return equipInventory(String(input.inventoryId ?? ''), ownerId);
  }
  return commitInventoryEquip(input);
}

Object.assign(window, { ftCommitInventoryEquip: commitInventoryEquipSeam, ftEquipOrLegacy: equipOrLegacy, ftEquipHands: equipHands, ftEquipInventory: equipInventory });
